'use client';

import { COMPANY_COUNTRIES, countryScopeOptions, isCompanyCountryCode } from '@/lib/countries';
import { CountryScopeControl } from '@/new_components/shared/CountryScopeControl';
import { z } from 'zod';
import {
  RangeSliderFilter,
} from './RangeSliderFilter';
import { FALLBACK_RANGE_EXTENTS, searchRangeExtentsSchema } from '@/lib/range-slider-scale';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  Bookmark,
  Building2,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Factory,
  Hash,
  Info,
  Loader2,
  MapPin,
  Search,
  TrendingUp,
  Users,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { generateCompanySlug } from '@/lib/company-utils';
import { cn } from '@/lib/utils';
import { SWISS_CANTON_NAMES } from '@/lib/company-utils';
import { useLocale } from '@/hooks/use-locale';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { CompanySearchResult } from './companySearchService';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  FacetCount,
  FilterLabel,
  FilterCheckbox,
  FIELD_LABEL_CLASS,
  FIELD_PLACEHOLDER_CLASS,
  INPUT_PLACEHOLDER_CLASS,
} from './FilterControls';
import {
  FilterGroup,
} from './FilterGroup';
import {
  controlLock,
  type FilterTier,
  groupLock as groupLockFor,
} from '@/lib/search-gating';
import {
  bundeslandList,
  courtList as COURT_LIST_SEED,
  personAgeRanges,
  type RangeDefinition,
  RECHTSFORM_OPTIONS,
  sortByFacetCount,
  filterLocationsForCountry,
} from './searchConstants';
import { allIndustryCategoriesSorted } from '@/lib/industry-categories';
import { LocaleLink } from '@/components/locale-link';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface NaceCode {
  code: string;
  description: string;
  level: number;
  /** Section letter (A–U) the code lives under. Source of truth for the
   * tree picker's division→section mapping — WZ 2025 diverges from NACE
   * Rev.2 (e.g. division 68 moved L→M), so hardcoded ranges go stale. */
  level1?: string | null;
}

const SECTION_ORDER = 'ABCDEFGHIJKLMNOPQRSTU';

/**
 * German-aware case fold: lowercase + ä→ae, ö→oe, ü→ue, ß→ss.
 * Mirrors the SQL de_fold() function so client-side dropdown filtering
 * matches the backend's matching behaviour ("Duess" finds "Düsseldorf").
 */
function deFold(s: string): string {
  return s
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss');
}

function naceSortKey(item: NaceCode): string {
  // Section row (level 1, single letter): order by SECTION_ORDER index.
  if (item.level === 1 && /^[A-Z]$/i.test(item.code)) {
    const idx = SECTION_ORDER.indexOf(item.code.toUpperCase());
    return `${String(idx === -1 ? 99 : idx).padStart(2, '0')}_000`;
  }
  // Any deeper code: section comes from the row's level1 column (DB truth).
  const sectionIdx = item.level1
    ? SECTION_ORDER.indexOf(item.level1.toUpperCase())
    : -1;
  const sectionPart = String(sectionIdx === -1 ? 99 : sectionIdx).padStart(2, '0');
  // Pad the leading numeric run so "10" sorts after "9" within a section.
  const leadingNum = item.code.match(/^(\d+)/);
  const numPart = leadingNum ? leadingNum[1].padStart(3, '0') : '000';
  return `${sectionPart}_${numPart}_${item.code}`;
}

export interface FilterParams {
  q: string;
  /** 'DE' | 'CH' | 'AT' | '' (global). Selector next to the keyword input. */
  country: string;
  city: string[];
  registerId: string;
  registerCourt: string;
  /** HRA / HRB / PR / VR / GnR / GsR. Empty string = no filter. */
  registerType: string;
  euid: string;
  Bundesland: string[];
  revenueMin?: number;
  revenueMax?: number;
  /**
   * Widen the revenue interval to also match companies with no revenue on
   * file. Off by default — only 2.6% of companies report revenue, so this
   * turns any interval into ~97% of the database. It exists because that
   * *is* a legitimate query ("small or unknown"), and because the backend
   * used to apply it silently for every bucket up to 5M.
   */
  revenueIncludeNull: boolean;
  profitMin?: number;
  profitMax?: number;
  /**
   * Bilanzsumme (balance-sheet total) buckets. Sits beside revenue rather
   * than replacing it: 822,065 companies carry a balance-sheet total
   * against 77,126 with revenue, because most German filers publish a
   * balance sheet without a full P&L.
   */
  aktivaMin?: number;
  aktivaMax?: number;
  /**
   * Fiscal year the company must have filed a report for. Staged like every
   * other control in its group and applied on Submit — it used to live in
   * the parent and search immediately, which meant "Clear" on the Size group
   * fired a search built from the URL and silently discarded the other
   * clears the user had just made.
   */
  financialYear?: number;
  employeesMin?: number;
  employeesMax?: number;
  ageRanges: string[];
  naceCodes: string[];
  industries: string[];
  /** 4-digit year. Converted to `${year}-01-01` server-side. Empty = no lower bound. */
  foundedAtFrom: string;
  /** 4-digit year. Converted to `${year}-12-31` server-side. Empty = no upper bound. */
  foundedAtTo: string;
  tags: string[];
  personName: string;
  rechtsformCodes: string[];
  cpvCode: string;
  /** Only return companies that have filed yearly metadata. */
  hasYearlyMetadata: boolean;
  /** External-registry memberships (OR-merged on the server). */
  memberships: string[];
  /** Company lifecycle buckets (OR-merged on the server). */
  legalStatus: string[];
  companySize: string[];
  /** EU public-procurement roles: winner | buyer (OR-merged on the server). */
  tenderRole: string[];
  /**
   * Founder / family / succession. Tri-state: `true`, `false`, or absent
   * for "no filter". `false` is a real selection (a board that has fully
   * turned over), not the default — see backend company_founder_profile.
   */
  familyManaged?: boolean;
  founderLed?: boolean;
  successionRisk?: boolean;
  /** Exact LEI lookup. */
  leiCode: string;
  /** Exact USt-IdNr lookup. */
  ustIdnr: string;
  /** True = only return companies with a known website. */
  hasWebsite: boolean;
  /** True = only return companies with a known email. */
  hasEmail: boolean;
}

interface CompanySearchSidebarProps {
  naceCodes: NaceCode[];
  cityList: string[];
  tagList: string[];
  onSubmit: (filters: FilterParams) => void;
  sort?: string;
  offset?: number;
  limit?: number;
  totalLimit?: number;
  onTotalLimitChange?: (limit: number) => void;
  /** Show the fiscal-year picker. The value itself rides in FilterParams. */
  showFinancialYear?: boolean;
  total?: number;
  onOffsetChange?: (offset: number) => void;
  filterParams?: FilterParams;
  isOpen?: boolean;
  onClose?: () => void;
  onCountryChange?: (country: string) => void;
  /** When true, the sidebar renders as a fullscreen overlay on mobile (with
   *  a top close button and the company-search input visible). Used by the
   *  /search page; other consumers (e.g. radar) keep the legacy inline
   *  dropdown behavior. */
  mobileFullscreen?: boolean;
  /**
   * Which filters this visitor may use — and, at `'public'`, whether the
   * mobile pagination block is offered at all.
   *
   * One prop rather than the `isAuthenticated` + `isPro` pair it replaces:
   * the two were separate booleans encoding a single ordered value, so every
   * consumer had to re-derive the tier and could disagree about it.
   */
  tier?: FilterTier;
  hideCompanySearch?: boolean;
  hidePersonSearch?: boolean;
  hideNaceCodes?: boolean;
  /**
   * Save the staged filter set.
   *
   * The sidebar holds *staged* state while the results on screen come from
   * the last applied search, so the page applies the filters as part of
   * saving them — otherwise a user who tweaked a control without pressing
   * Search would silently save something other than what they are looking
   * at. Absent (radar, /listen) the control is not rendered at all.
   */
  onSaveSearch?: (filters: FilterParams) => void;
  /** The user's saved searches, for the quick-load menu. */
  savedSearches?: { id: number; name: string; filters: FilterParams }[];
  onLoadSavedSearch?: (id: number) => void;
  /** True when the staged filters already match a saved search. */
  isCurrentSearchSaved?: boolean;
}

// CURRENT_YEAR backs the founded-between inputs' max attribute only; the
// age buckets come from personAgeRanges(), which derives its own.
const CURRENT_YEAR = new Date().getFullYear();

/**
 * Company lifecycle buckets. Disjoint and exhaustive — every company is in
 * exactly one — so the counts sum to the size of the index and ticking all
 * five is the same as ticking none.
 *
 * `descriptionKey` feeds the ⓘ next to the group label: "in liquidation" vs
 * "dissolved" vs "insolvent" are three different legal situations that all
 * read as "not trading normally", and users cannot be expected to know which
 * is which.
 */
const LEGAL_STATUS_OPTIONS = [
  { value: 'active', labelKey: 'legalStatusOptions.active', defaultLabel: 'Active' },
  {
    value: 'in_liquidation',
    labelKey: 'legalStatusOptions.inLiquidation',
    defaultLabel: 'In liquidation',
  },
  {
    value: 'insolvent',
    labelKey: 'legalStatusOptions.insolvent',
    defaultLabel: 'Insolvent',
  },
  {
    value: 'dissolved',
    labelKey: 'legalStatusOptions.dissolved',
    defaultLabel: 'Dissolved',
  },
  { value: 'deleted', labelKey: 'legalStatusOptions.deleted', defaultLabel: 'Deleted' },
] as const;

/**
 * HGB § 267 size classes.
 *
 * Unlike {@link LEGAL_STATUS_OPTIONS} these do **not** partition the register.
 * A company is classified only where what it was required to publish says so —
 * withholding the P&L is a relief only small companies may claim (§ 326 Abs. 1
 * HGB) — so a company that publishes its turnover, or that sits outside
 * §§ 264ff entirely (Swiss register, e.K., eGbR), has no class at all. Ticking
 * every box is therefore a real filter ("classified at all"), not a no-op, and
 * the counts do not sum to the result total.
 *
 * Three options, not the four statutory classes. Every inference runs
 * *downward* from a claimed relief, and no relief asserts largeness — a große
 * Kapitalgesellschaft is exactly the one that claims nothing — so `gross`
 * can never be assigned.
 */
const COMPANY_SIZE_OPTIONS = [
  {
    value: 'kleinst',
    labelKey: 'companySizeOptions.kleinst',
    defaultLabel: 'Micro-entity',
  },
  { value: 'klein', labelKey: 'companySizeOptions.klein', defaultLabel: 'Small' },
  {
    value: 'mittelgross',
    labelKey: 'companySizeOptions.mittelgross',
    defaultLabel: 'Medium-sized',
  },
] as const;

/** English fallbacks for the size-class ⓘ. Thresholds are the ones in force
 *  for fiscal years from 2024 (Delegated Directive (EU) 2023/2775); a company
 *  qualifies by not exceeding at least two of the three. */
const COMPANY_SIZE_HELP_EN: Record<string, string> = {
  kleinst:
    '§ 267a HGB — at most two of: €450k balance sheet, €900k turnover, 10 employees. Files a balance sheet only.',
  klein:
    '§ 267 (1) HGB — at most two of: €7.5m balance sheet, €15m turnover, 50 employees. Publishes no profit-and-loss statement.',
  mittelgross:
    '§ 267 (2) HGB — at most two of: €25m balance sheet, €50m turnover, 250 employees. May report gross profit instead of turnover.',
};

/** English fallbacks for the ⓘ tooltip, keyed by bucket. The i18n bundles
 *  carry the real copy; these keep the component readable on its own and
 *  render correctly if a key is ever missing. */
const LEGAL_STATUS_HELP_EN: Record<string, string> = {
  active: 'Trading normally — no insolvency or dissolution on record.',
  in_liquidation:
    'Dissolved and being wound up, with a Liquidator in office. Covers both a voluntary shareholder resolution and dissolution caused by insolvency.',
  insolvent:
    'Insolvency proceedings are open, or preliminary measures (vorläufige Insolvenzverwaltung) are in force.',
  dissolved:
    'Dissolved with no Liquidator in office, or an insolvency proceeding that has since been lifted (Verfahren aufgehoben).',
  deleted:
    'Removed from the Handelsregister entirely. Takes precedence over the other statuses.',
};

/**
 * EU public-procurement roles, from TED notices.
 *
 * Unlike {@link LEGAL_STATUS_OPTIONS} these do **not** partition the register:
 * they overlap (a company can be both), and ~99% of companies are in neither.
 * Ticking both therefore means "appears in TED at all" and is a genuine
 * filter, not the no-op that ticking every legal status is.
 *
 * `buyer` is intrinsically the smaller of the two by an order of magnitude,
 * and that is not a data gap: most contracting authorities are municipalities
 * or Anstalten des öffentlichen Rechts and have no Handelsregister entry to
 * be matched to in the first place.
 */
const TENDER_ROLE_OPTIONS = [
  {
    value: 'winner',
    labelKey: 'tenderRoleOptions.winner',
    defaultLabel: 'Won public contracts',
  },
  {
    value: 'buyer',
    labelKey: 'tenderRoleOptions.buyer',
    defaultLabel: 'Contracting authority',
  },
] as const;

/** English fallbacks for the ⓘ tooltip — see LEGAL_STATUS_HELP_EN. */
const TENDER_ROLE_HELP_EN: Record<string, string> = {
  winner:
    'Was awarded at least one public contract published in the EU tender database (TED).',
  buyer:
    'Published at least one tender as the contracting authority. Rare among registered companies — most public buyers are authorities with no Handelsregister entry.',
};

/** External-registry memberships the backend can OR-merge across. The
 *  `value` is the literal string the backend expects; `label` is what users
 *  see in the dropdown. `bbh` is expanded to its full name per product spec. */
const MEMBERSHIP_OPTIONS: { value: string; label: string }[] = [
  { value: 'Steuerberaterkammer', label: 'Steuerberaterkammer' },
  { value: 'Wirtschaftsprueferkammer', label: 'Wirtschaftsprüferkammer' },
  { value: 'bbh', label: 'Buchhalter und Bilanzbuchhalter Bundesverband' },
  { value: 'BaFin', label: 'BaFin' },
  { value: 'LEI', label: 'LEI' },
];

/** Register types. Stored as the literal value the backend expects.
 *  Empty value = no filter (handled in collectFilters/URL writer). */
const REGISTER_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'HRA', label: 'HRA — Handelsregister A' },
  { value: 'HRB', label: 'HRB — Handelsregister B' },
  { value: 'PR', label: 'PR — Partnerschaftsregister' },
  { value: 'VR', label: 'VR — Vereinsregister' },
  { value: 'GnR', label: 'GnR — Genossenschaftsregister' },
  { value: 'GsR', label: 'GsR — Gesellschaftsregister' },
  { value: 'CH-HR', label: 'CH-HR — Handelsregister (Schweiz)' },
  { value: 'AT-FB', label: 'FN — Firmenbuch (Österreich)' },
];

export default function CompanySearchSidebar({
  naceCodes,
  cityList,
  tagList,
  onSubmit,
  offset = 0,
  totalLimit = 50,
  showFinancialYear = false,
  total = 0,
  onOffsetChange,
  filterParams,
  isOpen = true,
  onClose,
  onCountryChange,
  mobileFullscreen = false,
  tier = 'public',
  hideCompanySearch = false,
  hidePersonSearch = false,
  hideNaceCodes = false,
  onSaveSearch,
  savedSearches,
  onLoadSavedSearch,
  isCurrentSearchSaved = false,
}: CompanySearchSidebarProps) {
  const { t, i18n } = useTranslation();
  const fmtLocale = i18n.resolvedLanguage || i18n.language || 'en';
  // Per-option company counts (mv_search_facets). Absent until the fetch
  // lands, and absent forever on a DB without migration 305 — every consumer
  // treats "no count" as unknown and renders as before, so this is decoration
  // that can never break the sidebar. Declared before the option lists
  // because several of them are now *ordered* by these counts.
  const [extents, setExtents] = useState(FALLBACK_RANGE_EXTENTS);
  const [facets, setFacets] = useState<Record<string, Record<string, number>>>({});
  useEffect(() => {
    fetch('/api/search/filters/facets')
      .then((res) => res.json())
      .then((data) => {
        const parsed = z.object({ facets: z.record(z.string(), z.record(z.string(), z.number())).optional(), extents: searchRangeExtentsSchema.optional() }).safeParse(data);
        if (parsed.success) {
          if (parsed.data.facets) setFacets(parsed.data.facets);
          if (parsed.data.extents) setExtents(parsed.data.extents);
        }
      })
      .catch(() => {});
  }, []);
  const facetCount = useCallback(
    (facet: string, value: string): number | undefined => facets[facet]?.[value],
    [facets],
  );
  /** Sum of several options — Rechtsform maps one label onto several codes. */
  const facetSum = useCallback(
    (facet: string, values: string[]): number | undefined => {
      const group = facets[facet];
      if (!group) return undefined;
      const present = values.filter((v) => v in group);
      if (present.length === 0) return undefined;
      return present.reduce((n, v) => n + group[v], 0);
    },
    [facets],
  );

  // Initialize from filterParams prop
  const [searchInput, setSearchInput] = useState(filterParams?.q ?? '');
  const [country, setCountry] = useState<string>(filterParams?.country ?? '');
  // The country scope (set via the header's flag selector, synced in through
  // filterParams) drives the location vocabulary: German states, Swiss
  // cantons, Austrian states, or (Global) every offered country's.
  // mv_search.bundesland_name holds canton / Austrian Bundesland names for
  // those rows, so the same backend parameter serves all — only the option
  // list and its label change. Austria joins Global with its search scope.
  const locationOptions = useMemo(() => {
    const cantons = Object.values(SWISS_CANTON_NAMES).sort();
    const options =
      country === 'CH'
        ? cantons
        : isCompanyCountryCode(country)
          ? COMPANY_COUNTRIES[country].regions
          : [
              ...bundeslandList,
              ...(COMPANY_COUNTRIES.AT.searchScope
                ? COMPANY_COUNTRIES.AT.regions
                : []),
              ...cantons,
            ];
    return sortByFacetCount(options, (b) => facetCount('bundesland', b));
  }, [country, facetCount]);
  const [selectedCities, setSelectedCities] = useState<string[]>(
    filterParams?.city ?? [],
  );
  const [cityOpen, setCityOpen] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const [currentRegisterId, setCurrentRegisterId] = useState(
    filterParams?.registerId ?? '',
  );
  const [currentRegisterCourt, setCurrentRegisterCourt] = useState(
    filterParams?.registerCourt ?? '',
  );
  const [currentRegisterType, setCurrentRegisterType] = useState(
    filterParams?.registerType ?? '',
  );
  const [currentEuid, setCurrentEuid] = useState(filterParams?.euid ?? '');
  const [hasYearlyMetadata, setHasYearlyMetadata] = useState<boolean>(
    filterParams?.hasYearlyMetadata ?? false,
  );
  const [selectedBundesland, setSelectedBundesland] = useState<string[]>(
    filterParams?.Bundesland ?? [],
  );
  const [bundeslandOpen, setBundeslandOpen] = useState(false);

  const [revenueMin, setRevenueMin] = useState<number | undefined>(filterParams?.revenueMin);
  const [revenueMax, setRevenueMax] = useState<number | undefined>(filterParams?.revenueMax);
  const [revenueIncludeNull, setRevenueIncludeNull] = useState<boolean>(
    filterParams?.revenueIncludeNull ?? false,
  );
  const [aktivaMin, setAktivaMin] = useState<number | undefined>(filterParams?.aktivaMin);
  const [aktivaMax, setAktivaMax] = useState<number | undefined>(filterParams?.aktivaMax);
  const [financialYear, setFinancialYear] = useState<number | undefined>(
    filterParams?.financialYear,
  );
  const [profitMin, setProfitMin] = useState<number | undefined>(filterParams?.profitMin);
  const [profitMax, setProfitMax] = useState<number | undefined>(filterParams?.profitMax);
  const [employeesMin, setEmployeesMin] = useState<number | undefined>(filterParams?.employeesMin);
  const [employeesMax, setEmployeesMax] = useState<number | undefined>(filterParams?.employeesMax);
  const [selectedAgeRanges, setSelectedAgeRanges] = useState<string[]>(
    filterParams?.ageRanges ?? [],
  );

  const [ageOpen, setAgeOpen] = useState(false);

  const [selectedNaceCodes, setSelectedNaceCodes] = useState<string[]>(
    filterParams?.naceCodes ?? [],
  );
  const [naceCodesOpen, setNaceCodesOpen] = useState(false);
  const [naceSearch, setNaceSearch] = useState('');
  const [expandedNaceNodes, setExpandedNaceNodes] = useState<Set<string>>(
    () => new Set(),
  );
  const naceListRef = useRef<HTMLDivElement>(null);

  const [industryOpen, setIndustryOpen] = useState(false);
  const allIndustries = useMemo(
    () =>
      sortByFacetCount(allIndustryCategoriesSorted(fmtLocale), (c) =>
        facetCount('industry', c.key),
      ),
    [fmtLocale, facetCount],
  );
  // Same treatment for the other unordered vocabularies. Rechtsform sums
  // its codes because one label can map onto several (GmbH is 221110 and
  // the coarse 221100).
  const rechtsformOptions = useMemo(
    () => sortByFacetCount(RECHTSFORM_OPTIONS, (o) => facetSum('rechtsform_code', o.codes)),
    [facetSum],
  );
  const membershipOptions = useMemo(
    () => sortByFacetCount(MEMBERSHIP_OPTIONS, (o) => facetCount('membership', o.value)),
    [facetCount],
  );
  const registerTypeOptions = useMemo(
    () => sortByFacetCount(REGISTER_TYPE_OPTIONS, (o) => facetCount('register_type', o.value)),
    [facetCount],
  );
  // Industries are an independent filter — sent to the backend as a separate
  // parameter that gets expanded to NACE divisions there. The NACE-Codes
  // selector below is unaffected by industry toggles, and vice versa.
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>(
    filterParams?.industries ?? [],
  );
  const toggleIndustry = useCallback((key: string) => {
    setSelectedIndustries((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }, []);

  const [foundedAtFrom, setFoundedAtFrom] = useState(
    filterParams?.foundedAtFrom ?? '',
  );
  const [foundedAtTo, setFoundedAtTo] = useState(
    filterParams?.foundedAtTo ?? '',
  );

  const [tags, setTags] = useState<string[]>(
    filterParams?.tags ?? [],
  );
  const [tagsOpen, setTagsOpen] = useState(false);
  const [tagSearch, setTagSearch] = useState('');

  const [personName, setPersonName] = useState(filterParams?.personName ?? '');
  const [cpvCode, setCpvCode] = useState(filterParams?.cpvCode ?? '');
  const [cpvCodesOpen, setCpvCodesOpen] = useState(false);
  const [cpvSearch, setCpvSearch] = useState('');
  const [cpvCodesList, setCpvCodesList] = useState<{ code: string; description: string }[]>([]);
  const cpvListRef = useRef<HTMLDivElement>(null);
  const cpvDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load top-level CPV codes on mount, then narrow on search
  useEffect(() => {
    fetch('/api/search/cpv-codes')
      .then((res) => res.json())
      .then((data) => { if (Array.isArray(data)) setCpvCodesList(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (cpvDebounceRef.current) clearTimeout(cpvDebounceRef.current);
    if (cpvSearch.length >= 2) {
      cpvDebounceRef.current = setTimeout(() => {
        fetch(`/api/search/cpv-codes?query=${encodeURIComponent(cpvSearch)}`)
          .then((res) => res.json())
          .then((data) => { if (Array.isArray(data)) setCpvCodesList(data); })
          .catch(() => {});
      }, 200);
    } else if (cpvSearch.length === 0) {
      fetch('/api/search/cpv-codes')
        .then((res) => res.json())
        .then((data) => { if (Array.isArray(data)) setCpvCodesList(data); })
        .catch(() => {});
    }
    return () => { if (cpvDebounceRef.current) clearTimeout(cpvDebounceRef.current); };
  }, [cpvSearch]);

  const [selectedRechtsformen, setSelectedRechtsformen] = useState<string[]>(
    filterParams?.rechtsformCodes ?? [],
  );
  const [rechtsformOpen, setRechtsformOpen] = useState(false);
  const [yearPickerOpen, setYearPickerOpen] = useState(false);
  const [registerTypeOpen, setRegisterTypeOpen] = useState(false);
  const [registerCourtOpen, setRegisterCourtOpen] = useState(false);
  const [registerCourtSearch, setRegisterCourtSearch] = useState('');

  // Seeded from the static list so the dropdown renders instantly, then
  // replaced by the live set from mv_search (ordered by company count) so
  // the options can never drift from what is actually searchable.
  const [courtList, setCourtList] = useState<string[]>(COURT_LIST_SEED);
  useEffect(() => {
    fetch('/api/search/filters/courts')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data?.courts) && data.courts.length > 0) {
          setCourtList(data.courts);
        }
      })
      .catch(() => {});
  }, []);
  // Accordion state: at most one filter group expanded at a time. Setting to
  // null collapses every group. Stored at the sidebar level so opening one
  // group implicitly closes any other.
  //
  // Company profile starts open: every filter in it is usable without an
  // account, so a first-time visitor lands on something they can act on
  // rather than on six collapsed headers, two of which are locked.
  const [openFilterGroup, setOpenFilterGroup] = useState<string | null>(
    'profile',
  );
  const handleGroupOpenChange = useCallback(
    (id: string) => (next: boolean) => {
      setOpenFilterGroup(next ? id : null);
    },
    [],
  );
  const [selectedMemberships, setSelectedMemberships] = useState<string[]>(
    filterParams?.memberships ?? [],
  );
  const [membershipsOpen, setMembershipsOpen] = useState(false);
  const [selectedLegalStatus, setSelectedLegalStatus] = useState<string[]>(
    filterParams?.legalStatus ?? [],
  );
  const [selectedCompanySize, setSelectedCompanySize] = useState<string[]>(
    filterParams?.companySize ?? [],
  );
  const [selectedTenderRole, setSelectedTenderRole] = useState<string[]>(
    filterParams?.tenderRole ?? [],
  );
  const [familyManaged, setFamilyManaged] = useState<boolean | undefined>(
    filterParams?.familyManaged,
  );
  const [founderLed, setFounderLed] = useState<boolean | undefined>(
    filterParams?.founderLed,
  );
  const [successionRisk, setSuccessionRisk] = useState<boolean | undefined>(
    filterParams?.successionRisk,
  );
  const [leiCode, setLeiCode] = useState(filterParams?.leiCode ?? '');
  const [ustIdnr, setUstIdnr] = useState(filterParams?.ustIdnr ?? '');
  const [hasWebsite, setHasWebsite] = useState<boolean>(
    filterParams?.hasWebsite ?? false,
  );
  const [hasEmail, setHasEmail] = useState<boolean>(
    filterParams?.hasEmail ?? false,
  );

  // The server strips anything above the tier regardless of what the UI
  // allows (lib/search-gating) — this only decides what to disable and how
  // to explain it.
  const lockOf = useCallback(
    (control: string) => controlLock(control, tier),
    [tier],
  );
  // A group whose every control is out of reach says so on its header. It
  // still opens: the disabled controls inside are what make signing up worth
  // it, so hiding them would sell nothing.
  const groupLock = useCallback(
    (group: string) => groupLockFor(group, tier),
    [tier],
  );

  const router = useRouter();
  const { locale } = useLocale();

  // Autocomplete state
  const [suggestions, setSuggestions] = useState<CompanySearchResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [sidebarActiveIndex, setSidebarActiveIndex] = useState(-1);
  // True only while sidebarActiveIndex was set via ArrowUp/ArrowDown. Enter
  // honors a highlighted row only after keyboard navigation — a dropdown that
  // opens under the mouse cursor fires onMouseEnter immediately, and Enter
  // would otherwise re-run the hovered "last search" instead of the input
  // text (same fix as LandingSearch).
  const keyboardNavRef = useRef(false);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Last searches state
  const [lastSearches, setLastSearches] = useState<string[]>([]);
  const [showLastSearches, setShowLastSearches] = useState(false);

  const fetchLastSearches = useCallback(async () => {
    try {
      const res = await fetch('/api/search/last-searches');
      if (res.ok) {
        const data = await res.json();
        const keywords = data.keywords ?? [];
        setLastSearches(keywords);
        return keywords;
      }
    } catch {
      // silently ignore
    }
    return [];
  }, []);

  const saveLastSearch = useCallback(async (keyword: string) => {
    try {
      await fetch('/api/search/last-searches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keyword }),
      });
    } catch {
      // silently ignore
    }
  }, []);

  const discardLastSearch = useCallback(
    async (keyword: string) => {
      setLastSearches((prev) => prev.filter((k) => k !== keyword));
      try {
        await fetch('/api/search/last-searches', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ keyword }),
        });
        const refreshed = await fetchLastSearches();
        if (refreshed.length === 0) setShowLastSearches(false);
      } catch {
        // silently ignore
      }
    },
    [fetchLastSearches],
  );

  // Structural parent of a NACE code, derived from the row + level.
  // Levels 1 = section letter (A–U), 2 = "NN" division, 3 = "NN.N",
  // 4 = "NN.NN", 5 = "NN.NN.N". The section letter for a division comes
  // from the row's level1 column — the DB's authoritative WZ 2025 mapping,
  // which diverges from NACE Rev.2.
  const getNaceParent = (item: NaceCode): string | null => {
    if (item.level <= 1) return null;
    if (item.level === 2) return item.level1 ?? null;
    if (item.level === 3) return item.code.slice(0, 2);
    if (item.level === 4) return item.code.slice(0, 4);
    if (item.level === 5) return item.code.slice(0, 5);
    return null;
  };

  // Indexes used both for "does this node have children?" (chevron rendering)
  // and for resolving the parent code of an arbitrary node's ancestor in the
  // visibility check below. Built once per dataset.
  const naceByCode = useMemo(() => {
    const m = new Map<string, NaceCode>();
    for (const c of naceCodes) m.set(c.code, c);
    return m;
  }, [naceCodes]);

  const naceCodesHaveChildren = useMemo(() => {
    const parents = new Set<string>();
    for (const c of naceCodes) {
      const p = getNaceParent(c);
      if (p) parents.add(p);
    }
    return parents;
  }, [naceCodes]);

  const sortedNaceCodes = useMemo(
    () =>
      [...naceCodes].sort((a, b) =>
        naceSortKey(a).localeCompare(naceSortKey(b)),
      ),
    [naceCodes],
  );

  // Visible list:
  //  - search active (≥2 chars): flat, every level matching code or description
  //  - otherwise: tree mode — top-level + children of every expanded ancestor
  const filteredNaceCodes = useMemo(() => {
    const q = naceSearch.trim().toLowerCase().replace(/-/g, '');
    if (q.length >= 2) {
      return sortedNaceCodes.filter((c) => {
        const code = c.code.toLowerCase().replace(/-/g, '');
        const desc = c.description.toLowerCase().replace(/-/g, '');
        return code.includes(q) || desc.includes(q);
      });
    }
    return sortedNaceCodes.filter((c) => {
      if (c.level === 1) return true;
      // Walk every ancestor; node visible iff each is expanded.
      let cursor: NaceCode | undefined = c;
      while (cursor && cursor.level > 1) {
        const parentCode = getNaceParent(cursor);
        if (!parentCode) return false;
        if (!expandedNaceNodes.has(parentCode)) return false;
        cursor = naceByCode.get(parentCode);
      }
      return true;
    });
  }, [sortedNaceCodes, naceSearch, expandedNaceNodes, naceByCode]);

  const toggleNaceExpanded = (code: string) => {
    setExpandedNaceNodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const previousAppliedFilters = useRef(filterParams);
  const preserveCountryEdits = Boolean(onCountryChange);

  // Staged edits leave filterParams unchanged. Sync whenever the applied URL
  // changes, including chip removals and back/forward navigation.
  useEffect(() => {
    const previous = previousAppliedFilters.current;
    previousAppliedFilters.current = filterParams;
    if (filterParams) {
      const nextCountry = filterParams.country ?? '';
      // A scope-only change applies immediately but keeps the other staged
      // edits. Narrow staged locations just like the applied result filters.
      const expected = previous && {
        ...previous,
        country: nextCountry,
        Bundesland: filterLocationsForCountry(previous.Bundesland, nextCountry),
        city: nextCountry ? [] : previous.city,
      };
      if (
        preserveCountryEdits &&
        previous &&
        previous.country !== nextCountry &&
        Object.keys(filterParams).every(
          (key) =>
            JSON.stringify(filterParams[key as keyof FilterParams]) ===
            JSON.stringify(expected?.[key as keyof FilterParams]),
        )
      ) {
        setCountry(nextCountry);
        setSelectedBundesland((staged) =>
          filterLocationsForCountry(staged, nextCountry),
        );
        if (nextCountry) setSelectedCities([]);
        return;
      }
      setSearchInput(filterParams.q);
      setSelectedCities(filterParams.city);
      setCurrentRegisterId(filterParams.registerId);
      setCurrentRegisterCourt(filterParams.registerCourt);
      setCurrentRegisterType(filterParams.registerType ?? '');
      setCurrentEuid(filterParams.euid);
      setHasYearlyMetadata(filterParams.hasYearlyMetadata ?? false);
      setSelectedBundesland(filterParams.Bundesland);
      setRevenueMin(filterParams.revenueMin);
      setRevenueMax(filterParams.revenueMax);
      setRevenueIncludeNull(filterParams.revenueIncludeNull ?? false);
      setAktivaMin(filterParams.aktivaMin);
      setAktivaMax(filterParams.aktivaMax);
      setFinancialYear(filterParams.financialYear);
      setProfitMin(filterParams.profitMin);
      setProfitMax(filterParams.profitMax);
      setEmployeesMin(filterParams.employeesMin);
      setEmployeesMax(filterParams.employeesMax);
      setSelectedAgeRanges(filterParams.ageRanges);
      setSelectedNaceCodes(filterParams.naceCodes);
      setSelectedRechtsformen(filterParams.rechtsformCodes);
      setSelectedIndustries(filterParams.industries ?? []);
      setFoundedAtFrom(filterParams.foundedAtFrom ?? '');
      setFoundedAtTo(filterParams.foundedAtTo ?? '');
      setTags(filterParams.tags ?? []);
      setPersonName(filterParams.personName ?? '');
      setCpvCode(filterParams.cpvCode ?? '');
      setSelectedMemberships(filterParams.memberships ?? []);
      setSelectedLegalStatus(filterParams.legalStatus ?? []);
      setSelectedCompanySize(filterParams.companySize ?? []);
      setCountry(filterParams.country ?? '');
      setSelectedTenderRole(filterParams.tenderRole ?? []);
      setFamilyManaged(filterParams.familyManaged);
      setFounderLed(filterParams.founderLed);
      setSuccessionRisk(filterParams.successionRisk);
      setLeiCode(filterParams.leiCode ?? '');
      setUstIdnr(filterParams.ustIdnr ?? '');
      setHasWebsite(filterParams.hasWebsite ?? false);
      setHasEmail(filterParams.hasEmail ?? false);
    }
  }, [filterParams, preserveCountryEdits]);

  // Fetch autocomplete suggestions via lightweight API route (same fast backend)
  const fetchSuggestions = useCallback((query: string) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    if (query.trim().length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceTimerRef.current = setTimeout(async () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setSuggestionsLoading(true);
      setShowSuggestions(true);
      try {
        const res = await fetch(
          `/api/companies/suggest?q=${encodeURIComponent(query)}&limit=5`,
          { signal: controller.signal },
        );
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();
        setSuggestions(data.results || []);
      } catch (err: unknown) {
        if (!(err instanceof Error && err.name === 'AbortError')) {
          setSuggestions([]);
        }
      } finally {
        setSuggestionsLoading(false);
      }
    }, 200);
  }, []);

  // Reset active index when suggestions or dropdown visibility change
  useEffect(() => {
    setSidebarActiveIndex(-1);
    keyboardNavRef.current = false;
  }, [suggestions, showSuggestions, showLastSearches]);

  // Scroll active item into view
  useEffect(() => {
    if (sidebarActiveIndex >= 0 && autocompleteRef.current) {
      const activeEl = autocompleteRef.current.querySelector(
        '[data-active="true"]',
      );
      if (activeEl) activeEl.scrollIntoView({ block: 'nearest' });
    }
  }, [sidebarActiveIndex]);

  // Close autocomplete / last searches on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
        setShowLastSearches(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Collect current filter state
  const collectFilters = useCallback(
    (): FilterParams => ({
      q: searchInput,
      city: selectedCities,
      registerId: currentRegisterId,
      registerCourt: currentRegisterCourt,
      registerType: currentRegisterType,
      euid: currentEuid,
      Bundesland: selectedBundesland,
      revenueMin,
      revenueMax,
      revenueIncludeNull,
      aktivaMin,
      aktivaMax,
      financialYear,
      profitMin,
      profitMax,
      employeesMin,
      employeesMax,
      ageRanges: selectedAgeRanges,
      naceCodes: selectedNaceCodes,
      industries: selectedIndustries,
      foundedAtFrom,
      foundedAtTo,
      tags,
      personName,
      rechtsformCodes: selectedRechtsformen,
      cpvCode,
      hasYearlyMetadata,
      memberships: selectedMemberships,
      legalStatus: selectedLegalStatus,
      companySize: selectedCompanySize,
      country,
      tenderRole: selectedTenderRole,
      familyManaged,
      founderLed,
      successionRisk,
      leiCode,
      ustIdnr,
      hasWebsite,
      hasEmail,
    }),
    [
      searchInput,
      selectedCities,
      currentRegisterId,
      currentRegisterCourt,
      currentRegisterType,
      currentEuid,
      selectedBundesland,
      revenueMin,
      revenueMax,
      revenueIncludeNull,
      aktivaMin,
      aktivaMax,
      financialYear,
      profitMin,
      profitMax,
      employeesMin,
      employeesMax,
      selectedAgeRanges,
      selectedNaceCodes,
      selectedIndustries,
      foundedAtFrom,
      foundedAtTo,
      tags,
      personName,
      selectedRechtsformen,
      cpvCode,
      hasYearlyMetadata,
      selectedMemberships,
      selectedLegalStatus,
      selectedCompanySize,
      country,
      selectedTenderRole,
      familyManaged,
      founderLed,
      successionRisk,
      leiCode,
      ustIdnr,
      hasWebsite,
      hasEmail,
    ],
  );

  const handleFormSubmit = useCallback(() => {
    setShowSuggestions(false);
    setShowLastSearches(false);
    const filters = collectFilters();
    if (filters.q.trim()) {
      saveLastSearch(filters.q.trim());
    }
    onSubmit(filters);
  }, [onSubmit, collectFilters, saveLastSearch]);

  const handleSuggestionClick = useCallback(
    (suggestion: CompanySearchResult) => {
      setShowSuggestions(false);
      setShowLastSearches(false);
      if (suggestion.name?.trim()) {
        saveLastSearch(suggestion.name.trim());
      }
      const slug = generateCompanySlug(
        {
          name: suggestion.name,
          sitz: suggestion.city,
          registerCourt: suggestion.registerCourt,
          registerType: suggestion.registerType,
          registerId: suggestion.registerId,
        },
        locale,
      );
      router.push(slug);
    },
    [router, saveLastSearch, locale],
  );

  const toggleSet = (
    val: string,
    setFn: React.Dispatch<React.SetStateAction<string[]>>,
  ) =>
    setFn((prev) =>
      prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val],
    );

  const removeSet = (
    val: string,
    setFn: React.Dispatch<React.SetStateAction<string[]>>,
  ) => setFn((prev) => prev.filter((x) => x !== val));

  const getAgeRangeLabel = (id: string) => {
    const labels: Record<string, string> = {
      'age-20-30': '20 – 30',
      'age-30-40': '30 – 40',
      'age-40-50': '40 – 50',
      'age-50-60': '50 – 60',
      'age-60-70': '60 – 70',
      'age-70+': '70+',
    };
    return labels[id] ?? id;
  };

  // Per-group active filter counts. Each non-empty field counts once. The
  // company-name and ID-lookup inputs sit outside any group and aren't counted.
  const locationCount =
    (selectedCities.length > 0 ? 1 : 0) +
    (selectedBundesland.length > 0 ? 1 : 0);
  const industryCount =
    (selectedNaceCodes.length > 0 ? 1 : 0) +
    (selectedIndustries.length > 0 ? 1 : 0) +
    (tags.length > 0 ? 1 : 0) +
    (cpvCode ? 1 : 0);
  const sizeCount =
    ((revenueMin !== undefined || revenueMax !== undefined) ? 1 : 0) +
    ((aktivaMin !== undefined || aktivaMax !== undefined) ? 1 : 0) +
    ((profitMin !== undefined || profitMax !== undefined) ? 1 : 0) +
    ((employeesMin !== undefined || employeesMax !== undefined) ? 1 : 0) +
    (financialYear != null ? 1 : 0) +
    (hasYearlyMetadata ? 1 : 0);
  const profileCount =
    (selectedRechtsformen.length > 0 ? 1 : 0) +
    (foundedAtFrom ? 1 : 0) +
    (foundedAtTo ? 1 : 0) +
    (selectedMemberships.length > 0 ? 1 : 0) +
    (selectedLegalStatus.length > 0 ? 1 : 0) +
    (selectedTenderRole.length > 0 ? 1 : 0) +
    (hasWebsite ? 1 : 0) +
    (hasEmail ? 1 : 0);
  const peopleCount =
    (personName ? 1 : 0) +
    (selectedAgeRanges.length > 0 ? 1 : 0) +
    (familyManaged !== undefined ? 1 : 0) +
    (founderLed !== undefined ? 1 : 0) +
    (successionRisk !== undefined ? 1 : 0);
  // LEI and USt-IdNr live here rather than under "Company profile": both are
  // exact identifier lookups like euId and the register number, not
  // descriptive attributes you browse by.
  const registerCount =
    (currentEuid ? 1 : 0) +
    (currentRegisterId ? 1 : 0) +
    (currentRegisterCourt ? 1 : 0) +
    (currentRegisterType ? 1 : 0) +
    (leiCode ? 1 : 0) +
    (ustIdnr ? 1 : 0);

  const clearLocation = useCallback(() => {
    setSelectedCities([]);
    setSelectedBundesland([]);
  }, []);
  const clearIndustry = useCallback(() => {
    setSelectedNaceCodes([]);
    setSelectedIndustries([]);
    setTags([]);
    setCpvCode('');
  }, []);
  const clearSize = useCallback(() => {
    setRevenueMin(undefined);
    setRevenueMax(undefined);
    setRevenueIncludeNull(false);
    setAktivaMin(undefined);
    setAktivaMax(undefined);
    setProfitMin(undefined);
    setProfitMax(undefined);
    setEmployeesMin(undefined);
    setEmployeesMax(undefined);
    setHasYearlyMetadata(false);
    setFinancialYear(undefined);
  }, []);
  const clearProfile = useCallback(() => {
    setSelectedRechtsformen([]);
    setFoundedAtFrom('');
    setFoundedAtTo('');
    setSelectedMemberships([]);
    setSelectedLegalStatus([]);
    setSelectedTenderRole([]);
    setHasWebsite(false);
    setHasEmail(false);
  }, []);
  const clearPeople = useCallback(() => {
    setPersonName('');
    setSelectedAgeRanges([]);
    setFamilyManaged(undefined);
    setFounderLed(undefined);
    setSuccessionRisk(undefined);
  }, []);
  const clearRegister = useCallback(() => {
    setCurrentEuid('');
    setCurrentRegisterId('');
    setCurrentRegisterCourt('');
    setCurrentRegisterType('');
    setLeiCode('');
    setUstIdnr('');
  }, []);

  // Valid financial years offered in the year picker. Backend supports the
  // last several years; we expose 2018 → currentYear.
  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    const ys: number[] = [];
    for (let y = now; y >= 2018; y--) ys.push(y);
    return ys;
  }, []);

  return (
    <aside
      id="search-filters"
      aria-label={t('search.filters')}
      className={cn(
        'shrink-0 bg-background',
        // Mobile behavior depends on mobileFullscreen:
        //   true  → fullscreen overlay when open / hidden when closed
        //           (ImmoScout-style filter screen, used by /search)
        //   false → full-width inline dropdown with max-height animation
        //           (legacy behavior, used by radar)
        mobileFullscreen
          ? isOpen
            ? // Anchor below the mobile header via the `--mobile-header-height`
              // CSS variable that HeaderMobile publishes via ResizeObserver.
              // Header (z-50) stays visible above the overlay (z-40); the
              // bottom safe-area padding clears the iOS home indicator.
              'fixed inset-x-0 bottom-0 top-[var(--mobile-header-height,140px)] z-40 flex flex-col pb-[env(safe-area-inset-bottom)]'
            : 'hidden'
          : cn(
              'overflow-y-auto w-full transition-[max-height] duration-300 ease-in-out',
              isOpen ? 'max-h-[9999px] border-b' : 'max-h-0',
            ),
        // Desktop: in-flow left panel, animate width — same in both modes.
        'lg:relative lg:inset-auto lg:z-auto lg:overflow-hidden lg:overflow-y-visible lg:h-full lg:w-auto lg:transition-[width,min-width,max-width] lg:duration-300 lg:ease-in-out',
        isOpen
          ? 'lg:flex lg:max-h-none lg:w-[380px] lg:min-w-[380px] lg:max-w-[380px] lg:border-b-0 lg:border-r'
          : 'lg:flex lg:max-h-none lg:w-0 lg:min-w-0 lg:max-w-0',
      )}
    >
      {/* Mobile-only top bar with a close button — fullscreen-overlay mode
          only. lg:hidden so desktop keeps the inline-panel chrome. */}
      {mobileFullscreen && onClose && (
        <div className="lg:hidden flex items-center justify-between gap-2 px-4 py-3 border-b shrink-0 bg-background">
          <h2 className="text-sm font-semibold">{t('search.filters')}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.buttons.close', { defaultValue: 'Close' })}
            className="inline-flex items-center justify-center w-11 h-11 rounded-full hover:bg-muted"
          >
            <X size={20} />
          </button>
        </div>
      )}
      {/* Inner wrapper keeps content at fixed width during desktop collapse */}
      <div
        className={cn(
          'flex flex-col w-full lg:w-[380px] lg:min-w-[380px]',
          // In fullscreen-overlay mode the wrapper needs to fill the aside so
          // the footer Search button stays pinned at the bottom. Legacy mode
          // keeps the natural-height layout for radar's inline dropdown.
          mobileFullscreen ? 'flex-1 min-h-0' : 'lg:h-full',
        )}
      >
        {/* Scrollable Filters. `overscroll-contain` stops iOS Safari from
            bubble-scrolling the page underneath when the user hits the top/
            bottom of the list inside the mobile filter overlay. */}
        <div className="flex-1 overflow-y-auto overscroll-contain min-h-0 p-4 flex flex-col">
          {/* Top-level unified keyword input — binds to `q` (the backend's
              unified keyword field). Funnels into eu_id → company-name →
              person-name resolution server-side. Autocomplete dropdown only
              shows company suggestions per /api/companies/suggest; person /
              euId terms are submitted via Enter. */}
          {!hideCompanySearch && (
            <div className="space-y-1.5 mb-3" ref={autocompleteRef}>
              <Label className={FIELD_LABEL_CLASS}>
                {t('search.sidebar.description')}
              </Label>
              <div className="relative">
                <Input
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value);
                    if (e.target.value.trim().length >= 3) {
                      setShowLastSearches(false);
                      fetchSuggestions(e.target.value);
                    } else if (e.target.value.length === 0) {
                      setShowSuggestions(false);
                      fetchLastSearches().then((keywords) => {
                        if (keywords.length > 0) setShowLastSearches(true);
                      });
                    } else {
                      setShowSuggestions(false);
                      setShowLastSearches(false);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (showSuggestions && suggestions.length > 0) {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        keyboardNavRef.current = true;
                        setSidebarActiveIndex(
                          (prev) => (prev + 1) % suggestions.length,
                        );
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        keyboardNavRef.current = true;
                        setSidebarActiveIndex((prev) =>
                          prev <= 0 ? suggestions.length - 1 : prev - 1,
                        );
                      } else if (e.key === 'Enter') {
                        e.preventDefault();
                        if (keyboardNavRef.current && sidebarActiveIndex >= 0) {
                          handleSuggestionClick(suggestions[sidebarActiveIndex]);
                        } else {
                          handleFormSubmit();
                        }
                      } else if (e.key === 'Escape') {
                        setShowSuggestions(false);
                        setSidebarActiveIndex(-1);
                      }
                    } else if (showLastSearches && lastSearches.length > 0) {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        keyboardNavRef.current = true;
                        setSidebarActiveIndex(
                          (prev) => (prev + 1) % lastSearches.length,
                        );
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        keyboardNavRef.current = true;
                        setSidebarActiveIndex((prev) =>
                          prev <= 0 ? lastSearches.length - 1 : prev - 1,
                        );
                      } else if (e.key === 'Enter') {
                        e.preventDefault();
                        if (keyboardNavRef.current && sidebarActiveIndex >= 0) {
                          const keyword = lastSearches[sidebarActiveIndex];
                          setSearchInput(keyword);
                          setShowLastSearches(false);
                        } else {
                          handleFormSubmit();
                        }
                      } else if (e.key === 'Escape') {
                        setShowLastSearches(false);
                        setSidebarActiveIndex(-1);
                      }
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      handleFormSubmit();
                    } else if (e.key === 'Escape') {
                      setShowSuggestions(false);
                      setShowLastSearches(false);
                    }
                  }}
                  onFocus={() => {
                    if (searchInput.trim().length >= 3) {
                      if (suggestions.length > 0) setShowSuggestions(true);
                    } else if (!searchInput || searchInput.length === 0) {
                      fetchLastSearches().then((keywords) => {
                        if (keywords.length > 0) setShowLastSearches(true);
                      });
                    }
                  }}
                  onClick={() => {
                    if (!searchInput || searchInput.length === 0) {
                      fetchLastSearches().then((keywords) => {
                        if (keywords.length > 0) setShowLastSearches(true);
                      });
                    }
                  }}
                  placeholder={t('search.sidebar.descriptionPlaceholder')}
                  className={`h-11 lg:h-9 text-base lg:text-sm ${INPUT_PLACEHOLDER_CLASS}`}
                />
                {/* Autocomplete dropdown */}
                {showSuggestions && searchInput.trim().length >= 3 && (
                  <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border bg-popover shadow-md overflow-hidden">
                    {suggestionsLoading ? (
                      <div className="flex items-center justify-center py-3 text-xs text-muted-foreground gap-2">
                        <Loader2 size={14} className="animate-spin" />
                        {t('search.sidebar.searching', {
                          defaultValue: 'Searching...',
                        })}
                      </div>
                    ) : suggestions.length === 0 ? (
                      <div className="py-3 px-3 text-xs text-muted-foreground text-center">
                        {t('search.sidebar.noSuggestionsFound', {
                          defaultValue: 'No companies found',
                        })}
                      </div>
                    ) : (
                      <ul className="max-h-[240px] overflow-y-auto py-1">
                        {suggestions.map((s, index) => (
                          <li
                            key={s.euId}
                            data-active={sidebarActiveIndex === index}
                            className={`flex items-center gap-2 px-3 py-2 text-xs cursor-pointer transition-colors ${sidebarActiveIndex === index ? 'bg-accent' : 'hover:bg-accent'}`}
                            onMouseEnter={() => setSidebarActiveIndex(index)}
                            onMouseDown={(e) => {
                              e.preventDefault(); // prevent blur
                              handleSuggestionClick(s);
                            }}
                          >
                            <span
                              className={`flex h-6 w-6 items-center justify-center rounded-md shrink-0 ${s.faviconUrl ? 'bg-muted dark:bg-white' : 'bg-muted'}`}
                            >
                              {s.faviconUrl ? (
                                <img
                                  src={s.faviconUrl}
                                  alt=""
                                  className="h-4 w-4 object-contain"
                                  onError={(e) => {
                                    const img = e.target as HTMLImageElement;
                                    img.style.display = 'none';
                                    const fallback =
                                      img.nextElementSibling as HTMLElement | null;
                                    if (fallback) {
                                      fallback.classList.remove('hidden');
                                      img.parentElement?.classList.remove(
                                        'dark:bg-white',
                                      );
                                    }
                                  }}
                                />
                              ) : null}
                              <Building2
                                className={`h-3.5 w-3.5 text-muted-foreground${s.faviconUrl ? ' hidden' : ''}`}
                              />
                            </span>
                            <span className="font-medium truncate">{s.name}</span>
                            {s.city && (
                              <span className="text-muted-foreground ml-auto shrink-0">
                                {s.city}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                {/* Recent searches dropdown */}
                {showLastSearches &&
                  !showSuggestions &&
                  lastSearches.length > 0 && (
                    <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border bg-popover shadow-md overflow-hidden">
                      <div className="py-1">
                        <div className="px-3 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                          {t('search.recentSearches', 'Recent Searches')}
                        </div>
                        {lastSearches.map((keyword, idx) => (
                          <div
                            key={`${keyword}-${idx}`}
                            data-active={sidebarActiveIndex === idx}
                            onMouseEnter={() => setSidebarActiveIndex(idx)}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setSearchInput(keyword);
                              setShowLastSearches(false);
                            }}
                            className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-xs transition-colors ${sidebarActiveIndex === idx ? 'bg-accent text-accent-foreground' : 'hover:bg-accent hover:text-accent-foreground'}`}
                          >
                            <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <span className="font-medium truncate flex-1">
                              {keyword}
                            </span>
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                discardLastSearch(keyword);
                              }}
                              className="p-0.5 rounded-md hover:bg-muted-foreground/20 text-muted-foreground shrink-0"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          )}

          {/* ─────────────────────── Filter groups ─────────────────────── */}

          {/* Group 1 — Company profile: Rechtsform, founding window,
              memberships, contactability. First and open by default: it is
              the group a visitor can use in full without an account. */}
          <FilterGroup
            id="profile"
            icon={Building2}
            label={t('search.sidebar.groups.profile', { defaultValue: 'Company profile' })}
            activeCount={profileCount}
            onClear={clearProfile}
            open={openFilterGroup === 'profile'}
            onOpenChange={handleGroupOpenChange('profile')}
            lock={groupLock('profile')}
          >
            {/* Rechtsform */}
            <div className="space-y-1.5">
              <Label className={FIELD_LABEL_CLASS}>
                {t('search.sidebar.rechtsform', { defaultValue: 'Legal form' })}
              </Label>
              <Popover open={rechtsformOpen} onOpenChange={setRechtsformOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
                  >
                    <div className="flex flex-wrap gap-1 w-full">
                      {selectedRechtsformen.length === 0 ? (
                        <span className={FIELD_PLACEHOLDER_CLASS}>
                          {t('search.sidebar.rechtsformPlaceholder', {
                            defaultValue: 'e.g. GmbH, AG…',
                          })}
                        </span>
                      ) : (
                        selectedRechtsformen.map((code) => {
                          const opt = RECHTSFORM_OPTIONS.find(
                            (o) => o.codes.join(',') === code,
                          );
                          return (
                            <Badge
                              key={code}
                              variant="secondary"
                              className="text-xs font-normal gap-1 pr-1 border border-foreground"
                            >
                              <span className="max-w-[220px] truncate">
                                {opt?.label ?? code}
                              </span>
                              <X
                                size={12}
                                className="cursor-pointer hover:bg-muted rounded-sm"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  removeSet(code, setSelectedRechtsformen);
                                }}
                              />
                            </Badge>
                          );
                        })
                      )}
                      <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                >
                  <Command>
                    <CommandInput
                      className={INPUT_PLACEHOLDER_CLASS}
                      placeholder={t('search.sidebar.searchRechtsform', {
                        defaultValue: 'Search legal forms…',
                      })}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {t('search.sidebar.noRechtsformFound', {
                          defaultValue: 'No legal form found.',
                        })}
                      </CommandEmpty>
                      <CommandGroup>
                        {rechtsformOptions.map((opt) => {
                          const key = opt.codes.join(',');
                          return (
                            <CommandItem
                              key={key}
                              value={opt.label}
                              keywords={opt.keywords}
                              onSelect={() =>
                                toggleSet(key, setSelectedRechtsformen)
                              }
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  selectedRechtsformen.includes(key)
                                    ? 'opacity-100'
                                    : 'opacity-0',
                                )}
                              />
                              <span className="truncate text-xs">
                                {opt.label}
                              </span>
                              <FacetCount
                                count={facetSum('rechtsform_code', opt.codes)}
                              />
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Founded between — two 4-digit year inputs. `inputMode="numeric"`
                triggers the numeric keypad on mobile. The backend gets
                ${from}-01-01 / ${to}-12-31 in SearchPageClient. */}
            <div className="space-y-1.5">
              <Label className={FIELD_LABEL_CLASS}>
                {t('search.sidebar.foundedBetween', {
                  defaultValue: 'Founded between',
                })}
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1800}
                  max={CURRENT_YEAR}
                  value={foundedAtFrom}
                  onChange={(e) =>
                    setFoundedAtFrom(e.target.value.replace(/\D/g, '').slice(0, 4))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleFormSubmit();
                    }
                  }}
                  aria-label={t('search.sidebar.foundedFrom', {
                    defaultValue: 'Founded from',
                  })}
                  placeholder="1990"
                  className={`flex-1 h-11 lg:h-9 text-base lg:text-sm ${INPUT_PLACEHOLDER_CLASS}`}
                />
                <span className="text-muted-foreground text-xs shrink-0">—</span>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1800}
                  max={CURRENT_YEAR}
                  value={foundedAtTo}
                  onChange={(e) =>
                    setFoundedAtTo(e.target.value.replace(/\D/g, '').slice(0, 4))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleFormSubmit();
                    }
                  }}
                  aria-label={t('search.sidebar.foundedTo', {
                    defaultValue: 'Founded to',
                  })}
                  placeholder={String(CURRENT_YEAR)}
                  className={`flex-1 h-11 lg:h-9 text-base lg:text-sm ${INPUT_PLACEHOLDER_CLASS}`}
                />
              </div>
            </div>

            {/* Memberships — multi-select. Backend OR-merges. Pro-gated:
                this is the one filter that comes from data we assembled
                ourselves (the professional-registry matcher), not from a
                public register, so it is the most valuable to bulk-extract
                and the least reproducible elsewhere. */}
            <div className="space-y-1.5">
              <FilterLabel
                lock={lockOf('memberships')}
                className={FIELD_LABEL_CLASS}
              >
                {t('search.sidebar.memberships', { defaultValue: 'Memberships' })}
              </FilterLabel>
              <Popover
                open={lockOf('memberships') ? false : membershipsOpen}
                onOpenChange={setMembershipsOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    disabled={!!lockOf('memberships')}
                    className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
                  >
                    <div className="flex flex-wrap gap-1 w-full">
                      {selectedMemberships.length === 0 ? (
                        <span className={FIELD_PLACEHOLDER_CLASS}>
                          {t('search.sidebar.membershipsPlaceholder', {
                            defaultValue: 'e.g. BaFin, Steuerberaterkammer…',
                          })}
                        </span>
                      ) : (
                        selectedMemberships.map((value) => {
                          const opt = MEMBERSHIP_OPTIONS.find(
                            (o) => o.value === value,
                          );
                          return (
                            <Badge
                              key={value}
                              variant="secondary"
                              className="text-xs font-normal gap-1 pr-1 border border-foreground"
                            >
                              <span className="max-w-[220px] truncate">
                                {opt?.label ?? value}
                              </span>
                              <X
                                size={12}
                                className="cursor-pointer hover:bg-muted rounded-sm"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  removeSet(value, setSelectedMemberships);
                                }}
                              />
                            </Badge>
                          );
                        })
                      )}
                      <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                >
                  <Command>
                    <CommandList>
                      <CommandGroup>
                        {membershipOptions.map((opt) => (
                          <CommandItem
                            key={opt.value}
                            value={opt.label}
                            onSelect={() =>
                              toggleSet(opt.value, setSelectedMemberships)
                            }
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                selectedMemberships.includes(opt.value)
                                  ? 'opacity-100'
                                  : 'opacity-0',
                              )}
                            />
                            <span className="truncate text-xs">{opt.label}</span>
                            <FacetCount count={facetCount('membership', opt.value)} />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Legal status — five disjoint buckets, resolved server-side
                from the merged Handelsregister + Insolvenzbekanntmachungen
                timeline. The ⓘ is not decoration: "in liquidation",
                "dissolved" and "insolvent" are three distinct legal states
                that all look like "not trading" from outside. */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-1">
                <FilterLabel
                  lock={lockOf('legalStatus')}
                  className={FIELD_LABEL_CLASS}
                >
                  {t('search.sidebar.legalStatus', {
                    defaultValue: 'Legal status',
                  })}
                </FilterLabel>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={t('search.sidebar.legalStatusInfoLabel', {
                          defaultValue: 'What do these statuses mean?',
                        })}
                        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground/70 hover:text-foreground"
                      >
                        <Info className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="right"
                      align="start"
                      className="max-w-[19rem] text-xs"
                    >
                      <dl className="space-y-1.5">
                        {LEGAL_STATUS_OPTIONS.map((opt) => (
                          <div key={opt.value}>
                            <dt className="font-medium">
                              {t(`search.sidebar.${opt.labelKey}`, {
                                defaultValue: opt.defaultLabel,
                              })}
                            </dt>
                            <dd className="text-muted-foreground">
                              {t(`search.sidebar.${opt.labelKey}Help`, {
                                defaultValue: LEGAL_STATUS_HELP_EN[opt.value],
                              })}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="space-y-0.5">
                {LEGAL_STATUS_OPTIONS.map((opt) => (
                  <FilterCheckbox
                    key={opt.value}
                    checked={selectedLegalStatus.includes(opt.value)}
                    onCheckedChange={(v) =>
                      setSelectedLegalStatus((prev) =>
                        v
                          ? [...prev, opt.value]
                          : prev.filter((x) => x !== opt.value),
                      )
                    }
                    lock={lockOf('legalStatus')}
                    label={t(`search.sidebar.${opt.labelKey}`, {
                      defaultValue: opt.defaultLabel,
                    })}
                    count={facetCount('legal_status', opt.value)}
                  />
                ))}
              </div>
            </div>

            {/* Company size — the § 267 HGB classes. Derived from what the
                company was legally required to publish, not from a figure it
                filed: withholding the profit-and-loss statement is a relief
                only small companies may claim. So this reaches ~761k
                companies that have no turnover on file at all, which is the
                whole point of it. Ticking every box still filters. */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-1">
                <FilterLabel
                  lock={lockOf('companySize')}
                  className={FIELD_LABEL_CLASS}
                >
                  {t('search.sidebar.companySize', {
                    defaultValue: 'Company size',
                  })}
                </FilterLabel>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={t('search.sidebar.companySizeInfoLabel', {
                          defaultValue: 'How is company size determined?',
                        })}
                        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground/70 hover:text-foreground"
                      >
                        <Info className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="right"
                      align="start"
                      className="max-w-[19rem] text-xs"
                    >
                      <dl className="space-y-1.5">
                        {COMPANY_SIZE_OPTIONS.map((opt) => (
                          <div key={opt.value}>
                            <dt className="font-medium">
                              {t(`search.sidebar.${opt.labelKey}`, {
                                defaultValue: opt.defaultLabel,
                              })}
                            </dt>
                            <dd className="text-muted-foreground">
                              {t(`search.sidebar.${opt.labelKey}Help`, {
                                defaultValue: COMPANY_SIZE_HELP_EN[opt.value],
                              })}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="space-y-0.5">
                {COMPANY_SIZE_OPTIONS.map((opt) => (
                  <FilterCheckbox
                    key={opt.value}
                    checked={selectedCompanySize.includes(opt.value)}
                    onCheckedChange={(v) =>
                      setSelectedCompanySize((prev) =>
                        v
                          ? [...prev, opt.value]
                          : prev.filter((x) => x !== opt.value),
                      )
                    }
                    lock={lockOf('companySize')}
                    label={t(`search.sidebar.${opt.labelKey}`, {
                      defaultValue: opt.defaultLabel,
                    })}
                    count={facetCount('company_size', opt.value)}
                  />
                ))}
              </div>
            </div>

            {/* Public procurement — two overlapping roles from EU TED
                notices. Unlike the legal-status buckets above, ticking both
                is a real filter (~1% of the register appears in TED at all),
                so there is no "select everything = no filter" shortcut here.
                The ⓘ carries the coverage caveat: TED data starts 2025. */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-1">
                <FilterLabel
                  lock={lockOf('tenderRole')}
                  className={FIELD_LABEL_CLASS}
                >
                  {t('search.sidebar.tenderRole', {
                    defaultValue: 'Public procurement',
                  })}
                </FilterLabel>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={t('search.sidebar.tenderRoleInfoLabel', {
                          defaultValue: 'What do these roles mean?',
                        })}
                        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground/70 hover:text-foreground"
                      >
                        <Info className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="right"
                      align="start"
                      className="max-w-[19rem] text-xs"
                    >
                      <dl className="space-y-1.5">
                        {TENDER_ROLE_OPTIONS.map((opt) => (
                          <div key={opt.value}>
                            <dt className="font-medium">
                              {t(`search.sidebar.${opt.labelKey}`, {
                                defaultValue: opt.defaultLabel,
                              })}
                            </dt>
                            <dd className="text-muted-foreground">
                              {t(`search.sidebar.${opt.labelKey}Help`, {
                                defaultValue: TENDER_ROLE_HELP_EN[opt.value],
                              })}
                            </dd>
                          </div>
                        ))}
                        <div>
                          <dd className="text-muted-foreground">
                            {t('search.sidebar.tenderRoleCoverage', {
                              defaultValue:
                                'Source: EU TED. Covers notices published from January 2025 onwards.',
                            })}
                          </dd>
                        </div>
                      </dl>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="space-y-0.5">
                {TENDER_ROLE_OPTIONS.map((opt) => (
                  <FilterCheckbox
                    key={opt.value}
                    checked={selectedTenderRole.includes(opt.value)}
                    onCheckedChange={(v) =>
                      setSelectedTenderRole((prev) =>
                        v
                          ? [...prev, opt.value]
                          : prev.filter((x) => x !== opt.value),
                      )
                    }
                    lock={lockOf('tenderRole')}
                    label={t(`search.sidebar.${opt.labelKey}`, {
                      defaultValue: opt.defaultLabel,
                    })}
                    count={facetCount('tender_role', opt.value)}
                  />
                ))}
              </div>
            </div>

            {/* Contact — boolean checkboxes. Headed rather than loose: they
                sit directly under the legal-status checkboxes and without a
                label of their own the two sets read as one list, so "Has
                Email" looked like another lifecycle state. We deliberately
                don't expose a "false" mode (only-without-X is rarely useful);
                unchecked = no filter applied. */}
            <div className="space-y-1.5">
              <FilterLabel
                lock={lockOf('hasWebsite') && lockOf('hasEmail')}
                className={FIELD_LABEL_CLASS}
              >
                {t('search.sidebar.contact', { defaultValue: 'Contact' })}
              </FilterLabel>
              <div className="space-y-0.5">
                <FilterCheckbox
                  checked={hasWebsite}
                  onCheckedChange={setHasWebsite}
                  lock={lockOf('hasWebsite')}
                  label={t('search.sidebar.hasWebsite', {
                    defaultValue: 'Has Website',
                  })}
                  count={facetCount('has_website', 'true')}
                />
                <FilterCheckbox
                  checked={hasEmail}
                  onCheckedChange={setHasEmail}
                  lock={lockOf('hasEmail')}
                  label={t('search.sidebar.hasEmail', {
                    defaultValue: 'Has Email',
                  })}
                  count={facetCount('has_email', 'true')}
                />
              </div>
            </div>
          </FilterGroup>

          {/* Group 2 — Location: City + Bundesland */}
          <FilterGroup
            id="location"
            icon={MapPin}
            label={t('search.sidebar.groups.location', { defaultValue: 'Location' })}
            activeCount={locationCount}
            onClear={clearLocation}
            open={openFilterGroup === 'location'}
            onOpenChange={handleGroupOpenChange('location')}
            lock={groupLock('location')}
          >
            {/* City */}
            <div className="space-y-1.5">
              <FilterLabel lock={lockOf('city')} className={FIELD_LABEL_CLASS}>
                {t('search.sidebar.city')}
              </FilterLabel>
              <Popover
                open={lockOf('city') ? false : cityOpen}
                onOpenChange={(open) => {
                  setCityOpen(open);
                  if (!open) setCitySearch('');
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    disabled={!!lockOf('city')}
                    className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
                  >
                    <div className="flex flex-wrap gap-1 w-full">
                      {selectedCities.length === 0 ? (
                        <span className={FIELD_PLACEHOLDER_CLASS}>
                          {t('search.sidebar.cityPlaceholder')}
                        </span>
                      ) : (
                        selectedCities.map((c) => (
                          <Badge
                            key={c}
                            variant="secondary"
                            className="text-xs font-normal gap-1 pr-1 border border-foreground"
                          >
                            <span className="max-w-[220px] truncate">{c}</span>
                            <X
                              size={12}
                              className="cursor-pointer hover:bg-muted rounded-sm"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                removeSet(c, setSelectedCities);
                              }}
                            />
                          </Badge>
                        ))
                      )}
                      <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                >
                  <Command shouldFilter={false}>
                    <CommandInput
                      className={INPUT_PLACEHOLDER_CLASS}
                      placeholder={t('search.sidebar.searchCities', {
                        defaultValue: 'Search cities...',
                      })}
                      value={citySearch}
                      onValueChange={setCitySearch}
                    />
                    <CommandList className="max-h-[272px]">
                      <CommandEmpty>
                        {t('search.sidebar.noCityFound', {
                          defaultValue: 'No city found.',
                        })}
                      </CommandEmpty>
                      <CommandGroup>
                        {cityList
                          .filter(
                            (c) =>
                              !citySearch ||
                              deFold(c).includes(deFold(citySearch)),
                          )
                          .slice(0, 100)
                          .map((c) => (
                            <CommandItem
                              key={c}
                              value={c}
                              onSelect={() => toggleSet(c, setSelectedCities)}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  selectedCities.includes(c)
                                    ? 'opacity-100'
                                    : 'opacity-0',
                                )}
                              />
                              <span className="truncate text-xs">{c}</span>
                              <FacetCount count={facetCount('city', c)} />
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Country, right above the Bundesland list it narrows. Applied
                at once (SearchPageClient's applyCountry), like the old header
                flag; every country is the default. */}
            {onCountryChange && (
              <CountryScopeControl
                value={country}
                options={['', ...countryScopeOptions('searchScope')]}
                onChange={onCountryChange}
              />
            )}

            {/* Bundesland */}
            <div className="space-y-1.5">
              <FilterLabel
                lock={lockOf('Bundesland')}
                className={FIELD_LABEL_CLASS}
              >
                {country === 'CH'
                  ? t('search.sidebar.canton', { defaultValue: 'Canton' })
                  : country === ''
                    ? t('search.sidebar.stateOrCanton', {
                        defaultValue: 'State / canton',
                      })
                    : t('search.sidebar.bundesland', {
                        defaultValue: 'Federal state',
                      })}
              </FilterLabel>
              <Popover
                open={lockOf('Bundesland') ? false : bundeslandOpen}
                onOpenChange={setBundeslandOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    disabled={!!lockOf('Bundesland')}
                    className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
                  >
                    <div className="flex flex-wrap gap-1 w-full">
                      {selectedBundesland.length === 0 ? (
                        <span className={FIELD_PLACEHOLDER_CLASS}>
                          {t('search.sidebar.statePlaceholder', {
                            defaultValue: 'e.g. Bavaria',
                          })}
                        </span>
                      ) : (
                        selectedBundesland.map((b) => (
                          <Badge
                            key={b}
                            variant="secondary"
                            className="text-xs font-normal gap-1 pr-1 border border-foreground"
                          >
                            <span className="max-w-[220px] truncate">{b}</span>
                            <X
                              size={12}
                              className="cursor-pointer hover:bg-muted rounded-sm"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                removeSet(b, setSelectedBundesland);
                              }}
                            />
                          </Badge>
                        ))
                      )}
                      <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                >
                  <Command>
                    <CommandInput
                      className={INPUT_PLACEHOLDER_CLASS}
                      placeholder={t('search.sidebar.searchStates', {
                        defaultValue: 'Search states...',
                      })}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {t('search.sidebar.noStateFound', {
                          defaultValue: 'No state found.',
                        })}
                      </CommandEmpty>
                      <CommandGroup>
                        {locationOptions.map((b) => (
                          <CommandItem
                            key={b}
                            value={b}
                            onSelect={() => toggleSet(b, setSelectedBundesland)}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                selectedBundesland.includes(b)
                                  ? 'opacity-100'
                                  : 'opacity-0',
                              )}
                            />
                            <span className="truncate text-xs">{b}</span>
                            <FacetCount count={facetCount('bundesland', b)} />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </FilterGroup>

          {/* Group 3 — Industry & activity: NACE, Industry, Tags, CPV */}
          <FilterGroup
            id="industry"
            icon={Factory}
            label={t('search.sidebar.groups.industry', { defaultValue: 'Industry & activity' })}
            activeCount={industryCount}
            onClear={clearIndustry}
            open={openFilterGroup === 'industry'}
            onOpenChange={handleGroupOpenChange('industry')}
            lock={groupLock('industry')}
          >
            {/* High-level Industry filter (maps to NACE divisions) */}
            <div className="space-y-1.5">
              <FilterLabel lock={lockOf('industries')} className={FIELD_LABEL_CLASS}>
                {t('search.sidebar.industry', { defaultValue: 'Industry' })}
              </FilterLabel>
              <Popover
                open={lockOf('industries') ? false : industryOpen}
                onOpenChange={setIndustryOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    disabled={!!lockOf('industries')}
                    className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
                  >
                    <div className="flex flex-wrap gap-1 w-full">
                      {selectedIndustries.length === 0 ? (
                        <span className={FIELD_PLACEHOLDER_CLASS}>
                          {t('search.sidebar.industryPlaceholder', {
                            defaultValue: 'Pick industry',
                          })}
                        </span>
                      ) : (
                        selectedIndustries.map((key) => {
                          const cat = allIndustries.find((c) => c.key === key);
                          return (
                            <Badge
                              key={key}
                              variant="secondary"
                              className="text-xs font-normal gap-1 pr-1 border border-foreground"
                            >
                              <span className="max-w-[220px] truncate">{cat?.label ?? key}</span>
                              <X
                                size={12}
                                className="cursor-pointer hover:bg-muted rounded-sm"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  toggleIndustry(key);
                                }}
                              />
                            </Badge>
                          );
                        })
                      )}
                      <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                >
                  <Command>
                    <CommandInput
                      className={`text-xs ${INPUT_PLACEHOLDER_CLASS}`}
                      placeholder={t('search.sidebar.searchIndustries', {
                        defaultValue: 'Search industries…',
                      })}
                    />
                    <CommandList className="max-h-[272px] overflow-y-auto">
                      <CommandEmpty>
                        {t('search.sidebar.noIndustryFound', {
                          defaultValue: 'No industry found.',
                        })}
                      </CommandEmpty>
                      <CommandGroup>
                        {allIndustries.map(({ key, label }) => (
                          <CommandItem
                            key={key}
                            value={label}
                            onSelect={() => toggleIndustry(key)}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                selectedIndustries.includes(key) ? 'opacity-100' : 'opacity-0',
                              )}
                            />
                            <span className="truncate text-xs">{label}</span>
                            <FacetCount count={facetCount('industry', key)} />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* WZ 2025 Code (sector tree picker) */}
            {!hideNaceCodes && (
              <div className="space-y-1.5">
                <FilterLabel lock={lockOf('naceCodes')} className={FIELD_LABEL_CLASS}>
                  {t('search.sidebar.naceCode', { defaultValue: 'WZ 2025 code' })}
                </FilterLabel>
                <Popover
                  open={lockOf('naceCodes') ? false : naceCodesOpen}
                  onOpenChange={(open) => {
                    setNaceCodesOpen(open);
                    if (!open) setNaceSearch('');
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      disabled={!!lockOf('naceCodes')}
                      className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
                    >
                      <div className="flex flex-wrap gap-1 w-full">
                        {selectedNaceCodes.length === 0 ? (
                          <span className={FIELD_PLACEHOLDER_CLASS}>
                            {t('search.sidebar.industriesPlaceholder')}
                          </span>
                        ) : (
                          selectedNaceCodes.map((code) => {
                            const nace = naceCodes.find((c) => c.code === code);
                            return (
                              <Badge
                                key={code}
                                variant="secondary"
                                className="text-xs font-normal gap-1 pr-1 border border-foreground"
                              >
                                <span className="font-mono text-muted-foreground">
                                  {code}
                                </span>
                                <span className="max-w-[220px] truncate">
                                  {nace?.description || ''}
                                </span>
                                <X
                                  size={12}
                                  className="cursor-pointer hover:bg-muted rounded-sm"
                                  onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    removeSet(code, setSelectedNaceCodes);
                                  }}
                                />
                              </Badge>
                            );
                          })
                        )}
                        <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
                      </div>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                  >
                    <Command shouldFilter={false}>
                      <CommandInput
                        className={`text-xs ${INPUT_PLACEHOLDER_CLASS}`}
                        placeholder={t('search.sidebar.searchSectors')}
                        value={naceSearch}
                        onValueChange={(v) => {
                          setNaceSearch(v);
                          if (naceListRef.current)
                            naceListRef.current.scrollTop = 0;
                        }}
                      />
                      <CommandList
                        ref={naceListRef}
                        className="h-[272px] overflow-y-auto"
                      >
                        <CommandEmpty>
                          {t('search.sidebar.noSectorFound')}
                        </CommandEmpty>
                        <CommandGroup>
                          {filteredNaceCodes.map((nace) => {
                            const inTreeMode =
                              naceSearch.trim().length < 2;
                            const hasChildren = naceCodesHaveChildren.has(
                              nace.code,
                            );
                            const isExpanded = expandedNaceNodes.has(nace.code);
                            const indent = inTreeMode
                              ? (nace.level - 1) * 12
                              : 0;
                            return (
                              <CommandItem
                                key={nace.code}
                                value={`${nace.code} ${nace.description}`}
                                onSelect={() =>
                                  toggleSet(nace.code, setSelectedNaceCodes)
                                }
                              >
                                <div
                                  className="flex items-center w-full"
                                  style={{ paddingLeft: indent }}
                                >
                                  {inTreeMode && hasChildren ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleNaceExpanded(nace.code);
                                      }}
                                      aria-label={
                                        isExpanded
                                          ? t('search.sidebar.collapse', {
                                              defaultValue: 'Collapse',
                                            })
                                          : t('search.sidebar.expand', {
                                              defaultValue: 'Expand',
                                            })
                                      }
                                      className="mr-1 flex h-4 w-4 items-center justify-center rounded hover:bg-muted shrink-0"
                                    >
                                      <ChevronRight
                                        size={12}
                                        className={cn(
                                          'transition-transform',
                                          isExpanded && 'rotate-90',
                                        )}
                                      />
                                    </button>
                                  ) : (
                                    <span className="mr-1 inline-block h-4 w-4 shrink-0" />
                                  )}
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4 shrink-0',
                                      selectedNaceCodes.includes(nace.code)
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                    )}
                                  />
                                  <span className="font-mono text-xs text-muted-foreground mr-1.5 shrink-0">
                                    {nace.code}
                                  </span>
                                  <span className="truncate text-xs">
                                    {nace.description}
                                  </span>
                                  <FacetCount count={facetCount('nace', nace.code)} />
                                </div>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {/* Tags (products, risks, opportunities) */}
            <div className="space-y-1.5">
              <FilterLabel lock={lockOf('tags')} className={FIELD_LABEL_CLASS}>
                {t('search.sidebar.tags', {
                  defaultValue: 'Tags',
                })}
              </FilterLabel>
              <Popover
                open={lockOf('tags') ? false : tagsOpen}
                onOpenChange={(open) => {
                  setTagsOpen(open);
                  if (!open) setTagSearch('');
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    disabled={!!lockOf('tags')}
                    className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
                  >
                    <div className="flex flex-wrap gap-1 w-full">
                      {tags.length === 0 ? (
                        <span className={FIELD_PLACEHOLDER_CLASS}>
                          {t('search.sidebar.tagsPlaceholder', {
                            defaultValue: 'e.g. Software, Supply chains, Consulting…',
                          })}
                        </span>
                      ) : (
                        tags.map((kw) => (
                          <Badge
                            key={kw}
                            variant="secondary"
                            className="text-xs font-normal gap-1 pr-1 border border-foreground"
                          >
                            <span className="max-w-[220px] truncate">{kw}</span>
                            <X
                              size={12}
                              className="cursor-pointer hover:bg-muted rounded-sm"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                removeSet(kw, setTags);
                              }}
                            />
                          </Badge>
                        ))
                      )}
                      <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                >
                  <Command shouldFilter={false}>
                    <CommandInput
                      className={INPUT_PLACEHOLDER_CLASS}
                      placeholder={t('search.sidebar.searchTags', {
                        defaultValue: 'Search tags...',
                      })}
                      value={tagSearch}
                      onValueChange={setTagSearch}
                    />
                    <CommandList className="max-h-[272px]">
                      <CommandEmpty>
                        {t('search.sidebar.noTagFound', {
                          defaultValue: 'No tag found.',
                        })}
                      </CommandEmpty>
                      <CommandGroup>
                        {tagList
                          .filter(
                            (kw) =>
                              !tagSearch ||
                              kw
                                .toLowerCase()
                                .includes(tagSearch.toLowerCase()),
                          )
                          .slice(0, 100)
                          .map((kw) => (
                            <CommandItem
                              key={kw}
                              value={kw}
                              onSelect={() => toggleSet(kw, setTags)}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  tags.includes(kw)
                                    ? 'opacity-100'
                                    : 'opacity-0',
                                )}
                              />
                              <span className="truncate text-xs">{kw}</span>
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* CPV-Code */}
            <div className="space-y-1.5">
              <FilterLabel lock={lockOf('cpvCode')} className={FIELD_LABEL_CLASS}>
                {t('search.sidebar.cpvCode', { defaultValue: 'CPV code' })}
              </FilterLabel>
              <Popover
                open={lockOf('cpvCode') ? false : cpvCodesOpen}
                onOpenChange={(open) => {
                  setCpvCodesOpen(open);
                  if (!open) setCpvSearch('');
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    disabled={!!lockOf('cpvCode')}
                    className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
                  >
                    <div className="flex flex-wrap gap-1 w-full">
                      {!cpvCode ? (
                        <span className={FIELD_PLACEHOLDER_CLASS}>
                          {t('search.sidebar.cpvCodePlaceholder', {
                            defaultValue: 'e.g. 45000000 Construction work',
                          })}
                        </span>
                      ) : (
                        <Badge
                          variant="secondary"
                          className="text-xs font-normal gap-1 pr-1 border border-foreground"
                        >
                          <span className="font-mono text-muted-foreground">
                            {cpvCode}
                          </span>
                          <span className="max-w-[180px] truncate">
                            {cpvCodesList.find((c) => c.code === cpvCode)
                              ?.description || ''}
                          </span>
                          <X
                            size={12}
                            className="cursor-pointer hover:bg-muted rounded-sm"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setCpvCode('');
                            }}
                          />
                        </Badge>
                      )}
                      <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                >
                  <Command shouldFilter={false}>
                    <CommandInput
                      className={`text-xs ${INPUT_PLACEHOLDER_CLASS}`}
                      placeholder={t('search.sidebar.searchCpvCodes', {
                        defaultValue: 'Search CPV codes (min. 2 digits)…',
                      })}
                      value={cpvSearch}
                      onValueChange={(v) => {
                        setCpvSearch(v);
                        if (cpvListRef.current)
                          cpvListRef.current.scrollTop = 0;
                      }}
                    />
                    <CommandList
                      ref={cpvListRef}
                      className="h-[272px] overflow-y-auto"
                    >
                      <CommandEmpty>
                        {t('search.sidebar.noCpvFound', {
                          defaultValue: 'No CPV code found.',
                        })}
                      </CommandEmpty>
                      <CommandGroup>
                        {cpvCodesList.map((cpv) => (
                          <CommandItem
                            key={cpv.code}
                            value={`${cpv.code} ${cpv.description}`}
                            onSelect={() => {
                              setCpvCode(cpv.code);
                              setCpvCodesOpen(false);
                              setCpvSearch('');
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                cpvCode === cpv.code
                                  ? 'opacity-100'
                                  : 'opacity-0',
                              )}
                            />
                            <span className="font-mono text-xs text-muted-foreground mr-1.5">
                              {cpv.code}
                            </span>
                            <span className="truncate text-xs">
                              {cpv.description}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </FilterGroup>

          {/* Group 4 — Size & financials: Revenue, Bilanzsumme, Profit,
              Employees, filing year, filings-only */}
          <FilterGroup
            id="size"
            icon={TrendingUp}
            label={t('search.sidebar.groups.size', { defaultValue: 'Size & financials' })}
            activeCount={sizeCount}
            onClear={clearSize}
            open={openFilterGroup === 'size'}
            onOpenChange={handleGroupOpenChange('size')}
            lock={groupLock('size')}
          >
            <RangeSliderFilter
              label={<FilterLabel lock={lockOf('revenueMin')} className={FIELD_LABEL_CLASS}>{t('search.sidebar.revenue', { defaultValue: 'Revenue' })}</FilterLabel>}
              min={revenueMin} max={revenueMax}
              onChange={(min, max) => { setRevenueMin(min); setRevenueMax(max); }}
              maxPos={extents.revenue_max}
              employees={false}
              lock={lockOf('revenueMin')}
            />

            {/* Only meaningful once a revenue band is picked — on its own
                it is not a filter, and the backend ignores it. Shown only
                then so it doesn't read as an independent toggle. It is
                also not counted in `sizeCount`: it modifies the revenue
                filter rather than being one. */}
            {(revenueMin !== undefined || revenueMax !== undefined) && (
              <label className="flex items-start gap-2 cursor-pointer select-none -mt-0.5">
                <Checkbox
                  className="mt-0.5"
                  checked={revenueIncludeNull}
                  disabled={!!lockOf('revenueMin')}
                  onCheckedChange={(v) => setRevenueIncludeNull(v === true)}
                />
                <span className="text-xs text-muted-foreground leading-snug">
                  {t('search.sidebar.revenueIncludeNull', {
                    defaultValue:
                      'Also include companies with no revenue on file',
                  })}
                </span>
              </label>
            )}

            {/* Bilanzsumme — a peer of revenue, not a replacement. It is the
                broadest size axis the product has (822,065 companies vs
                revenue's 77,126) because most German filers publish a
                balance sheet without a full P&L, so a user who gets nothing
                from a revenue band will usually get results here. */}
            <RangeSliderFilter
              label={<FilterLabel lock={lockOf('aktivaMin')} className={FIELD_LABEL_CLASS}>{t('search.sidebar.aktiva', { defaultValue: 'Balance sheet total' })}</FilterLabel>}
              min={aktivaMin} max={aktivaMax}
              onChange={(min, max) => { setAktivaMin(min); setAktivaMax(max); }}
              maxPos={extents.aktiva_max}
              employees={false}
              lock={lockOf('aktivaMin')}
            />

            <RangeSliderFilter
              label={<FilterLabel lock={lockOf('profitMin')} className={FIELD_LABEL_CLASS}>{t('search.sidebar.profit', { defaultValue: 'Profit' })}</FilterLabel>}
              min={profitMin} max={profitMax}
              onChange={(min, max) => { setProfitMin(min); setProfitMax(max); }}
              maxPos={extents.profit_max}
              maxAbsNeg={Math.abs(extents.profit_min)}
              employees={false}
              lock={lockOf('profitMin')}
            />

            <RangeSliderFilter
              label={<FilterLabel lock={lockOf('employeesMin')} className={FIELD_LABEL_CLASS}>{t('search.sidebar.employees', { defaultValue: 'Employee Count' })}</FilterLabel>}
              min={employeesMin} max={employeesMax}
              onChange={(min, max) => { setEmployeesMin(min); setEmployeesMax(max); }}
              maxPos={extents.employees_max}
              employees={true}
              lock={lockOf('employeesMin')}
            />

            {/* Financial year — only rendered when the parent exposes the
                year setter; otherwise the picker is meaningless.

                Now backed by `mv_search.financial_years @> ARRAY[Y]`, i.e.
                "the company filed a report covering Y". It used to compare
                against the single latest-filing year, which meant picking
                2020 selected the ~31,021 companies that *stopped* filing in
                2020 rather than the ~484,503 that filed for it. */}
            {showFinancialYear && (
              <div className="space-y-1.5">
                <FilterLabel
                  lock={lockOf('financialYear')}
                  className={FIELD_LABEL_CLASS}
                >
                  {t('search.sidebar.financialYear', { defaultValue: 'Has report for year' })}
                </FilterLabel>
                <Popover
                  open={lockOf('financialYear') ? false : yearPickerOpen}
                  onOpenChange={setYearPickerOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      disabled={!!lockOf('financialYear')}
                      className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
                    >
                      <div className="flex flex-wrap gap-1 w-full">
                        {financialYear ? (
                          <Badge
                            variant="secondary"
                            className="text-xs font-normal gap-1 pr-1 border border-foreground"
                          >
                            <span className="font-mono">{financialYear}</span>
                            <X
                              size={12}
                              className="cursor-pointer hover:bg-muted rounded-sm"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                setFinancialYear(undefined);
                              }}
                            />
                          </Badge>
                        ) : (
                          <span className={FIELD_PLACEHOLDER_CLASS}>
                            {t('search.sidebar.selectYear')}
                          </span>
                        )}
                        <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
                      </div>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                  >
                    <Command>
                      <CommandList className="max-h-[272px]">
                        <CommandGroup>
                          {yearOptions.map((y) => (
                            <CommandItem
                              key={y}
                              value={String(y)}
                              onSelect={() => {
                                setFinancialYear(y);
                                setYearPickerOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  financialYear === y
                                    ? 'opacity-100'
                                    : 'opacity-0',
                                )}
                              />
                              <span className="font-mono text-xs">{y}</span>
                              <FacetCount count={facetCount('financial_year', String(y))} />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {/* Filings-only toggle — backed by hasYearlyMetadata. Useful as a
                quick "only show companies with financials" gate when the user
                cares about revenue/profit comparability. */}
            <FilterCheckbox
              checked={hasYearlyMetadata}
              onCheckedChange={setHasYearlyMetadata}
              lock={lockOf('hasYearlyMetadata')}
              label={t('search.sidebar.hasYearlyMetadata', {
                defaultValue: 'Only companies with filings',
              })}
              count={facetCount('has_filings', 'true')}
            />
          </FilterGroup>

          {/* Group 5 — People: person name + avg board birth-year age */}
          {!hidePersonSearch && (
            <FilterGroup
              id="people"
              icon={Users}
              label={t('search.sidebar.groups.people', { defaultValue: 'People' })}
              activeCount={peopleCount}
              onClear={clearPeople}
              open={openFilterGroup === 'people'}
              onOpenChange={handleGroupOpenChange('people')}
              lock={groupLock('people')}
            >
              {/* Person Name */}
              <div className="space-y-1.5">
                <FilterLabel lock={lockOf('personName')} className={FIELD_LABEL_CLASS}>
                  {t('search.sidebar.personName', {
                    defaultValue: 'People',
                  })}
                </FilterLabel>
                <Input
                  value={personName}
                  disabled={!!lockOf('personName')}
                  onChange={(e) => setPersonName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleFormSubmit();
                    }
                  }}
                  placeholder={t('search.sidebar.personNamePlaceholder', {
                    defaultValue: 'e.g. Max Mustermann',
                  })}
                  className={`h-11 lg:h-9 text-base lg:text-sm ${INPUT_PLACEHOLDER_CLASS}`}
                />
              </div>

              {/* Avg board age (avgBirthYear) */}
              <FilterRangeInput
                label={t('search.sidebar.personAge', {
                  defaultValue: 'Age (people)',
                })}
                open={ageOpen}
                setOpen={setAgeOpen}
                selected={selectedAgeRanges}
                setSelected={setSelectedAgeRanges}
                ranges={personAgeRanges()}
                getLabel={(id) => getAgeRangeLabel(id)}
                toggle={(id) => toggleSet(id, setSelectedAgeRanges)}
                placeholder={t('search.sidebar.personAgePlaceholder', {
                  defaultValue: 'e.g. 30 – 50',
                })}
                counts={facets['age']}
              lock={lockOf('ageRanges')}
            />

              {/* Founder / family / succession, derived from the register's
                  board history rather than read off it — so each carries a
                  hover explanation the way the badges on the company page
                  do. None of these three partition the register: a company
                  whose history we could not parse is in neither bucket, so
                  the counts do not add up to the index size.

                  The backend params stay tri-state (`founder_led=false` is
                  still valid on the API and reachable by URL); the sidebar
                  simply does not offer the negative as a checkbox. */}
              <div className="space-y-1.5">
                <FilterLabel
                  lock={lockOf('familyManaged')}
                  className={FIELD_LABEL_CLASS}
                >
                  {t('search.sidebar.founderProfile', {
                    defaultValue: 'Ownership & succession',
                  })}
                </FilterLabel>
                <div className="space-y-0.5">
                  <FilterCheckbox
                    checked={familyManaged === true}
                    onCheckedChange={(v) =>
                      setFamilyManaged(v ? true : undefined)
                    }
                    lock={lockOf('familyManaged')}
                    label={t('search.sidebar.familyManaged', {
                      defaultValue: 'Family business',
                    })}
                    hint={t('search.sidebar.familyManagedHint', {
                      defaultValue:
                        'Two or more members of one family hold an ownership or governing role — shared surname, a surname carried in the company name, or two generations on the board.',
                    })}
                    count={facetCount('family_managed', 'true')}
                  />
                  <FilterCheckbox
                    checked={founderLed === true}
                    onCheckedChange={(v) => setFounderLed(v ? true : undefined)}
                    lock={lockOf('founderLed')}
                    label={t('search.sidebar.founderLed', {
                      defaultValue: 'Founder-led',
                    })}
                    hint={t('search.sidebar.founderLedHint', {
                      defaultValue:
                        'Someone who held a role at the first register entry is still in office. Says nothing about ownership — a founder who stepped back but still owns the company does not count.',
                    })}
                    count={facetCount('founder_led', 'true')}
                  />
                  <FilterCheckbox
                    checked={successionRisk === true}
                    onCheckedChange={(v) =>
                      setSuccessionRisk(v ? true : undefined)
                    }
                    lock={lockOf('successionRisk')}
                    label={t('search.sidebar.successionRisk', {
                      defaultValue: 'Succession due',
                    })}
                    hint={t('search.sidebar.successionRiskHint', {
                      defaultValue:
                        'Founder-led and the longest-serving founder has reached 65. Founders with no birth date on record are excluded rather than assumed young.',
                    })}
                    count={facetCount('succession_risk', 'true')}
                  />
                </div>
              </div>
            </FilterGroup>
          )}

          {/* Group 6 — Register & identifiers: euId, registerId, registerCourt,
              registerType. The unified keyword input (q) sits above all groups
              at the top of the sidebar — see the block right under the
              "Scrollable Filters" comment. */}
          <FilterGroup
            id="register"
            icon={Hash}
            label={t('search.sidebar.groups.register', { defaultValue: 'Register & identifiers' })}
            activeCount={registerCount}
            onClear={clearRegister}
            open={openFilterGroup === 'register'}
            onOpenChange={handleGroupOpenChange('register')}
            lock={groupLock('register')}
          >
            {/* euId — direct lookup */}
            <div className="space-y-1.5">
              <Label className={FIELD_LABEL_CLASS}>
                {t('search.sidebar.euid', { defaultValue: 'European Unique Identifier' })}
              </Label>
              <Input
                value={currentEuid}
                onChange={(e) => setCurrentEuid(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleFormSubmit();
                  }
                }}
                placeholder={t('search.sidebar.euidPlaceholder')}
                className={`h-11 lg:h-9 text-base lg:text-sm font-mono ${INPUT_PLACEHOLDER_CLASS}`}
              />
            </div>

            {/* Register ID — digits only. The HR* prefix lives in
                `registerType`; the numeric tail is what the backend matches
                against `register_id`. `inputMode="numeric"` brings up the
                phone keypad on mobile. */}
            <div className="space-y-1.5">
              <Label className={FIELD_LABEL_CLASS}>
                {t('search.sidebar.registerId', { defaultValue: 'Register ID' })}
              </Label>
              <Input
                type="text"
                inputMode="numeric"
                pattern="\d*"
                value={currentRegisterId}
                onChange={(e) =>
                  setCurrentRegisterId(e.target.value.replace(/\D/g, ''))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleFormSubmit();
                  }
                }}
                placeholder={t('search.sidebar.registerIdPlaceholder', {
                  defaultValue: 'e.g. 100484',
                })}
                className={`h-11 lg:h-9 text-base lg:text-sm font-mono ${INPUT_PLACEHOLDER_CLASS}`}
              />
            </div>

            {/* Register Court — a dropdown, not free text. The backend
                matches `register_court = $1` exactly, and the stored
                spellings are the register's own: "Berlin (Charlottenburg)",
                "Frankfurt am Main", "Oldenburg (Oldenburg)". As an input
                this silently returned zero hits for anything typed the
                obvious way. Options come from the MV via
                /api/search/filters/courts, so the list can't drift from
                what is actually searchable. */}
            <div className="space-y-1.5">
              <Label className={FIELD_LABEL_CLASS}>
                {t('search.sidebar.registerCourt', { defaultValue: 'Register Court' })}
              </Label>
              <Popover
                open={registerCourtOpen}
                onOpenChange={(open) => {
                  setRegisterCourtOpen(open);
                  if (!open) setRegisterCourtSearch('');
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
                  >
                    <div className="flex flex-wrap gap-1 w-full">
                      {!currentRegisterCourt ? (
                        <span className={FIELD_PLACEHOLDER_CLASS}>
                          {t('search.sidebar.registerCourtPlaceholder', {
                            defaultValue: 'e.g. Stuttgart',
                          })}
                        </span>
                      ) : (
                        <Badge
                          variant="secondary"
                          className="text-xs font-normal gap-1 pr-1 border border-foreground"
                        >
                          <span className="max-w-[220px] truncate">
                            {currentRegisterCourt}
                          </span>
                          <X
                            size={12}
                            className="cursor-pointer hover:bg-muted rounded-sm"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setCurrentRegisterCourt('');
                            }}
                          />
                        </Badge>
                      )}
                      <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                >
                  <Command shouldFilter={false}>
                    <CommandInput
                      className={INPUT_PLACEHOLDER_CLASS}
                      placeholder={t('search.sidebar.searchCourts', {
                        defaultValue: 'Search courts...',
                      })}
                      value={registerCourtSearch}
                      onValueChange={setRegisterCourtSearch}
                    />
                    <CommandList className="max-h-[272px]">
                      <CommandEmpty>
                        {t('search.sidebar.noCourtFound', {
                          defaultValue: 'No court found.',
                        })}
                      </CommandEmpty>
                      <CommandGroup>
                        {/* de_fold so "Duren"/"Koln" find Düren/Köln, and
                            substring so "Charlottenburg" finds the Berlin
                            entry by its suffix. */}
                        {courtList
                          .filter(
                            (c) =>
                              !registerCourtSearch ||
                              deFold(c).includes(deFold(registerCourtSearch)),
                          )
                          .map((c) => (
                            <CommandItem
                              key={c}
                              value={c}
                              onSelect={() => {
                                setCurrentRegisterCourt(
                                  c === currentRegisterCourt ? '' : c,
                                );
                                setRegisterCourtOpen(false);
                                setRegisterCourtSearch('');
                              }}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  currentRegisterCourt === c
                                    ? 'opacity-100'
                                    : 'opacity-0',
                                )}
                              />
                              <span className="truncate text-xs">{c}</span>
                              <FacetCount count={facetCount('register_court', c)} />
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Register Type — HRA / HRB / PR / VR / GnR / GsR. */}
            <div className="space-y-1.5">
              <Label className={FIELD_LABEL_CLASS}>
                {t('search.sidebar.registerType', { defaultValue: 'Register Type' })}
              </Label>
              <Popover open={registerTypeOpen} onOpenChange={setRegisterTypeOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
                  >
                    <div className="flex flex-wrap gap-1 w-full">
                      {!currentRegisterType ? (
                        <span className={FIELD_PLACEHOLDER_CLASS}>
                          {t('search.sidebar.registerTypePlaceholder', {
                            defaultValue: 'e.g. HRB',
                          })}
                        </span>
                      ) : (
                        <Badge
                          variant="secondary"
                          className="text-xs font-normal gap-1 pr-1 border border-foreground"
                        >
                          <span className="max-w-[220px] truncate">
                            {REGISTER_TYPE_OPTIONS.find(
                              (o) => o.value === currentRegisterType,
                            )?.label ?? currentRegisterType}
                          </span>
                          <X
                            size={12}
                            className="cursor-pointer hover:bg-muted rounded-sm"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setCurrentRegisterType('');
                            }}
                          />
                        </Badge>
                      )}
                      <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                >
                  <Command>
                    <CommandList>
                      <CommandGroup>
                        {registerTypeOptions.map((opt) => (
                          <CommandItem
                            key={opt.value}
                            value={opt.value}
                            onSelect={() => {
                              setCurrentRegisterType(opt.value);
                              setRegisterTypeOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                currentRegisterType === opt.value
                                  ? 'opacity-100'
                                  : 'opacity-0',
                              )}
                            />
                            <span className="truncate text-xs">{opt.label}</span>
                            <FacetCount count={facetCount('register_type', opt.value)} />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* LEI Code — exact lookup. Independent of the LEI membership
                filter under Company profile: this matches a specific 20-char
                identifier; that one matches "company has any active LEI". */}
            <div className="space-y-1.5">
              <Label className={FIELD_LABEL_CLASS}>
                {t('search.sidebar.leiCode', { defaultValue: 'LEI Code' })}
              </Label>
              <Input
                value={leiCode}
                onChange={(e) => setLeiCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleFormSubmit();
                  }
                }}
                placeholder={t('search.sidebar.leiCodePlaceholder', {
                  defaultValue: 'e.g. 529900T8BM49AURSDO55',
                })}
                maxLength={20}
                className={`h-11 lg:h-9 text-base lg:text-sm font-mono ${INPUT_PLACEHOLDER_CLASS}`}
              />
            </div>

            {/* USt-IdNr — German VAT number. Fixed `DE` prefix sits inside
                the field as a decorative label; the user only types digits.
                Paste of "DE123456789" naturally drops the DE since the regex
                strips every non-digit. Backend gets `DE${digits}` from
                SearchPageClient. */}
            <div className="space-y-1.5">
              <FilterLabel lock={lockOf('ustIdnr')} className={FIELD_LABEL_CLASS}>
                {t('search.sidebar.ustIdnr', { defaultValue: 'USt-IdNr.' })}
              </FilterLabel>
              <div className="relative">
                <span
                  aria-hidden
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base lg:text-sm font-mono text-muted-foreground select-none"
                >
                  DE
                </span>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  value={ustIdnr}
                  disabled={!!lockOf('ustIdnr')}
                  onChange={(e) =>
                    setUstIdnr(e.target.value.replace(/\D/g, ''))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleFormSubmit();
                    }
                  }}
                  placeholder={t('search.sidebar.ustIdnrPlaceholder', {
                    defaultValue: '123456789',
                  })}
                  className={`h-11 lg:h-9 text-base lg:text-sm font-mono pl-9 ${INPUT_PLACEHOLDER_CLASS}`}
                />
              </div>
            </div>
          </FilterGroup>

        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t bg-background/95 supports-[backdrop-filter]:bg-background/90 backdrop-blur-xs flex flex-col gap-3 shrink-0">
          <div className="flex items-center gap-2">
            {onSaveSearch ? (
              <div className="flex flex-1 items-stretch">
                <Button
                  variant="outline"
                  onClick={() => onSaveSearch(collectFilters())}
                  className={`h-11 lg:h-9 flex-1 rounded-lg text-sm gap-1.5 ${
                    savedSearches && savedSearches.length > 0
                      ? 'rounded-r-none border-r-0'
                      : ''
                  }`}
                >
                  {isCurrentSearchSaved ? (
                    <>
                      <Check size={15} className="text-emerald-600" />
                      {t('search.sidebar.searchSaved', { defaultValue: 'Saved' })}
                    </>
                  ) : (
                    <>
                      <Bookmark size={15} />
                      {t('search.sidebar.saveSearch', { defaultValue: 'Save search' })}
                    </>
                  )}
                </Button>
                {savedSearches && savedSearches.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        aria-label={t('savedSearches.openMenu', {
                          defaultValue: 'Open saved searches',
                        })}
                        className="h-11 lg:h-9 rounded-lg rounded-l-none px-2"
                      >
                        <ChevronDown size={15} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-64">
                      <DropdownMenuLabel>
                        {t('savedSearches.title', { defaultValue: 'Saved searches' })}
                      </DropdownMenuLabel>
                      {savedSearches.map((s) => (
                        <DropdownMenuItem
                          key={s.id}
                          onClick={() => onLoadSavedSearch?.(s.id)}
                          className="truncate"
                        >
                          {s.name}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <LocaleLink href="/my-lists?tab=searches">
                          {t('savedSearches.manageAll', { defaultValue: 'Manage all' })}
                        </LocaleLink>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            ) : (
              <div className="flex-1" />
            )}
            <Button
              onClick={handleFormSubmit}
              className="flex-1 h-11 lg:h-9 rounded-lg bg-black hover:bg-neutral-800 text-white font-medium gap-1.5 text-sm"
            >
              <Search size={16} />
              {t('search.sidebar.searchButton')}
            </Button>
          </div>

          {/* Pagination & per-page — logged-in users only */}
          {tier !== 'public' &&
            onOffsetChange &&
            (() => {
              const currentPage = Math.floor(offset / totalLimit) + 1;
              const totalPages = Math.ceil(Math.min(total, 5000) / totalLimit);

              // Build page numbers: 1 ... (current-1) current (current+1) ... last
              const pages: (number | '...')[] = [];
              if (totalPages <= 7) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
              } else {
                pages.push(1);
                if (currentPage > 3) pages.push('...');
                for (
                  let i = Math.max(2, currentPage - 1);
                  i <= Math.min(totalPages - 1, currentPage + 1);
                  i++
                ) {
                  pages.push(i);
                }
                if (currentPage < totalPages - 2) pages.push('...');
                pages.push(totalPages);
              }

              return (
                // Desktop pagination now lives in the results bottom bar
                // (DataGridPagination); keep this only for the mobile overlay.
                <div className="flex flex-col gap-2 lg:hidden">
                  {/* Page numbers */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-1 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        disabled={currentPage === 1}
                        onClick={() =>
                          onOffsetChange((currentPage - 2) * totalLimit)
                        }
                      >
                        <ChevronLeft size={16} />
                      </Button>
                      {pages.map((p, i) =>
                        p === '...' ? (
                          <span
                            key={`dots-${i}`}
                            className="text-xs text-muted-foreground px-1"
                          >
                            …
                          </span>
                        ) : (
                          <Button
                            key={p}
                            variant={p === currentPage ? 'primary' : 'outline'}
                            size="sm"
                            className="h-8 w-8 p-0 text-xs"
                            onClick={() => onOffsetChange((p - 1) * totalLimit)}
                          >
                            {p}
                          </Button>
                        ),
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        disabled={currentPage === totalPages}
                        onClick={() => onOffsetChange(currentPage * totalLimit)}
                      >
                        <ChevronRight size={16} />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })()}
        </div>
      </div>
    </aside>
  );
}

function FilterRangeInput({
  label,
  open,
  setOpen,
  selected,
  setSelected,
  ranges,
  getLabel,
  toggle,
  placeholder,
  counts,
  lock = null,
}: {
  label: string;
  open: boolean;
  setOpen: (op: boolean) => void;
  selected: string[];
  setSelected: (val: string[]) => void;
  ranges: RangeDefinition[];
  getLabel: (id: string, r: RangeDefinition[]) => string;
  toggle: (id: string) => void;
  placeholder?: string;
  /** bucket id -> company count; absent entries render no count. */
  counts?: Record<string, number>;
  /** Tier this control needs, when the visitor lacks it. */
  lock?: 'user' | 'pro' | null;
}) {
  const removeItem = (id: string) => {
    setSelected(selected.filter((x) => x !== id));
  };

  return (
    <div className="space-y-1.5">
      <FilterLabel lock={lock} className={FIELD_LABEL_CLASS}>
        {label}
      </FilterLabel>
      <Popover open={lock ? false : open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            disabled={!!lock}
            aria-expanded={open}
            className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
          >
            <div className="flex flex-wrap gap-1 w-full">
              {selected.length === 0 ? (
                <span
                  className={FIELD_PLACEHOLDER_CLASS}
                  suppressHydrationWarning
                >
                  {placeholder ?? `e.g. ${getLabel(ranges[1].id, ranges)}`}
                </span>
              ) : (
                selected.map((id) => (
                  <Badge
                    key={id}
                    variant="secondary"
                    className="text-xs font-normal gap-1 pr-1 border border-foreground"
                  >
                    <span className="max-w-[220px] truncate">
                      {getLabel(id, ranges)}
                    </span>
                    <X
                      size={12}
                      className="cursor-pointer hover:bg-muted rounded-sm"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        removeItem(id);
                      }}
                    />
                  </Badge>
                ))
              )}
              <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
        >
          <Command>
            <CommandList>
              <CommandGroup>
                {ranges.map((range) => {
                  // A global count of 0 means no company anywhere carries
                  // this band, so selecting it can only ever return nothing.
                  // Only the bucketed facets can be 0 — the others are
                  // GROUP BY-derived and simply have no row when empty.
                  // Never disable an already-selected band, or the user
                  // cannot clear it.
                  const count = counts?.[range.id];
                  const isEmpty = count === 0 && !selected.includes(range.id);
                  return (
                    <CommandItem
                      key={range.id}
                      value={getLabel(range.id, ranges)}
                      disabled={isEmpty}
                      onSelect={() => {
                        if (!isEmpty) toggle(range.id);
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          selected.includes(range.id)
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                      <span className={cn(isEmpty && 'text-muted-foreground/50')}>
                        {getLabel(range.id, ranges)}
                      </span>
                      <FacetCount count={count} />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
